import Logo from "@/assets/logo.svg?react"
import Deepl from "@/assets/deepl.svg?react"
import AccountBalanceWalletIcon from "@/assets/account-balance-wallet-icon.svg?react"
import AcuteIcon from "@/assets/acute-icon.svg?react"
import DocumentTranslationIcon from "@/assets/document-translation-icon.svg?react"
import img1 from "@/assets/img1.png"
import img2 from "@/assets/img2.png"
import img3 from "@/assets/img3.png"
import img4 from "@/assets/img4.png"
import PremiumIcon from "@/assets/premium-icon.svg?react"
// import SmartDisplayIcon from "@/assets/smart-display-icon.svg?react"
import TargetIcon from "@/assets/target-icon.svg?react"
import { Selectable, SelectItem, SelectSeparator } from "@beringlab/ui"
import { format } from "date-fns"
import { TFunction } from "i18next"
import { Languages } from "lucide-react"
import { Trans } from "react-i18next"
import { createSelectItems } from "./language-select-items"
import { NotSupportedGroup } from "./not-supported-group"
import {
  DomainTypeEnum,
  FeedbackForm,
  FileQueue,
  KakaoTranslateLanguageTypeEnum,
  LegalTranslateLanguageTypeEnum,
  MachineTranslationForm,
  PatentTranslateLanguageTypeEnum,
  TranslateLanguageTypeEnum,
  TranslationEngineEnum,
  TranslationModeEnum,
  TranslationStatusEnum,
} from "./translation.types"
import { isDev } from "@/shared/lib/env/env"

/**
 * Returns the list of domains visible in the translation UI.
 * Kakao and Medical are only available on dev server.
 */
export const isDomainVisible = (domain: string) =>
  isDev ||
  (domain !== DomainTypeEnum.Medical && domain !== DomainTypeEnum.Kakao)

export const getVisibleDomains = (): Exclude<DomainTypeEnum, typeof DomainTypeEnum.Business>[] =>
  Object.values(DomainTypeEnum).filter(
    (d) =>
      d !== DomainTypeEnum.Business &&
      isDomainVisible(d)
  )

export enum TranslationQueryKeysEnum {
  FetchGlossaryList = "fetchGlossaryList",
  FetchGlossaryDetail = "fetchGlossaryDetail",
  FetchAlternativeTranslationForEachSegment = "fetchAlternativeTranslationForEachSegment",
  FetchSmartGlossaries = "fetchSmartGlossaries",
  FetchCatWorkspace = "fetchCatWorkspace",
  FetchAssignedCatWorkspaces = "fetchAssignedCatWorkspaces",
  FetchCatSegmentQE = "fetchCatSegmentQE",
  FetchCatQALatestRun = "fetchCatQALatestRun",
  FetchCatReviewLatestRun = "fetchCatReviewLatestRun",
  FetchCatSegmentComments = "fetchCatSegmentComments",
  FetchCatSegmentEditHistory = "fetchCatSegmentEditHistory",
  FetchDocumentVersions = "fetchDocumentVersions",
  FetchAssemblyStatus = "fetchAssemblyStatus",
  FetchDocumentTranslationProgress = "fetchDocumentTranslationProgress",
}

export enum GlossaryProcessEnum {
  SelectFromList,
  Select,
  Manual,
  Detail,
  Upload,
}

export const DEFAULT_FEEDBACK_FORM: FeedbackForm = {
  content: null,
  reportedAt: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
  reportingUrl: "",
  userId: "",
  liked: false,
}

export const sortLanguagesByPriority = <T extends string>(
  languages: T[],
  priorityLanguages: readonly T[],
  languageMenu: Record<T, string>
): T[] => {
  const priority = languages
    .filter((lang) => priorityLanguages.includes(lang))
    .sort((a, b) => priorityLanguages.indexOf(a) - priorityLanguages.indexOf(b))

  const others = languages
    .filter((lang) => !priorityLanguages.includes(lang))
    .sort((a, b) => languageMenu[a].localeCompare(languageMenu[b]))

  return [...priority, ...others]
}

export const getLanguageList = (
  t: TFunction
): Selectable<TranslateLanguageTypeEnum>[] => {
  return [
    { label: t("mt.detect"), value: TranslateLanguageTypeEnum.Auto },
    { label: t("common.korean"), value: TranslateLanguageTypeEnum.Ko },
    { label: t("common.japanese"), value: TranslateLanguageTypeEnum.Ja },
    { label: t("common.english"), value: TranslateLanguageTypeEnum.En },
    { label: t("common.chinese2"), value: TranslateLanguageTypeEnum.Zh },
    { label: t("common.chinese3"), value: TranslateLanguageTypeEnum.ZhHant },
    { label: t("common.indonesian"), value: TranslateLanguageTypeEnum.Id },
    { label: t("common.french"), value: TranslateLanguageTypeEnum.Fr },
    { label: t("common.spanish"), value: TranslateLanguageTypeEnum.Es },
    { label: t("common.german"), value: TranslateLanguageTypeEnum.De },
  ]
}

export const getLanguageMenu = (
  t: TFunction
): Record<TranslateLanguageTypeEnum, string> => {
  return {
    [TranslateLanguageTypeEnum.Auto]: t("mt.detect"),
    [TranslateLanguageTypeEnum.Zh]: t("common.chinese2"),
    [TranslateLanguageTypeEnum.En]: t("common.english"),
    [TranslateLanguageTypeEnum.Fr]: t("common.french"),
    [TranslateLanguageTypeEnum.Pt]: t("common.portuguese"),
    [TranslateLanguageTypeEnum.Es]: t("common.spanish"),
    [TranslateLanguageTypeEnum.Ja]: t("common.japanese"),
    [TranslateLanguageTypeEnum.Tr]: t("common.turkish"),
    [TranslateLanguageTypeEnum.Ru]: t("common.russian"),
    [TranslateLanguageTypeEnum.Ar]: t("common.arabic"),
    [TranslateLanguageTypeEnum.Ko]: t("common.korean"),
    [TranslateLanguageTypeEnum.Th]: t("common.thai"),
    [TranslateLanguageTypeEnum.It]: t("common.italian"),
    [TranslateLanguageTypeEnum.De]: t("common.german"),
    [TranslateLanguageTypeEnum.Vi]: t("common.vietnamese"),
    [TranslateLanguageTypeEnum.Ms]: t("common.malay"),
    [TranslateLanguageTypeEnum.Id]: t("common.indonesian"),
    [TranslateLanguageTypeEnum.Tl]: t("common.filipino"),
    [TranslateLanguageTypeEnum.Hi]: t("common.hindi"),
    [TranslateLanguageTypeEnum.ZhHant]: t("common.chinese3"),
    [TranslateLanguageTypeEnum.Pl]: t("common.polish"),
    [TranslateLanguageTypeEnum.Cs]: t("common.czech"),
    [TranslateLanguageTypeEnum.Nl]: t("common.dutch"),
    [TranslateLanguageTypeEnum.Km]: t("common.khmer"),
    [TranslateLanguageTypeEnum.My]: t("common.burmese"),
    [TranslateLanguageTypeEnum.Fa]: t("common.persian"),
    [TranslateLanguageTypeEnum.Gu]: t("common.gujarati"),
    [TranslateLanguageTypeEnum.Ur]: t("common.urdu"),
    [TranslateLanguageTypeEnum.Te]: t("common.telugu"),
    [TranslateLanguageTypeEnum.Mr]: t("common.marathi"),
    [TranslateLanguageTypeEnum.He]: t("common.hebrew"),
    [TranslateLanguageTypeEnum.Bn]: t("common.bengali"),
    [TranslateLanguageTypeEnum.Ta]: t("common.tamil"),
    [TranslateLanguageTypeEnum.Uk]: t("common.ukrainian"),
    [TranslateLanguageTypeEnum.Bo]: t("common.tibetan"),
    [TranslateLanguageTypeEnum.Kk]: t("common.kazakh"),
    [TranslateLanguageTypeEnum.Mn]: t("common.mongolian"),
    [TranslateLanguageTypeEnum.Ug]: t("common.uyghur"),
    [TranslateLanguageTypeEnum.Yue]: t("common.cantonese"),
  }
}

export const getLegalLanguageMenu = (
  t: TFunction
): Record<LegalTranslateLanguageTypeEnum, string> => {
  return {
    [LegalTranslateLanguageTypeEnum.Auto]: t("mt.detect"),
    [LegalTranslateLanguageTypeEnum.Ara]: t("common.arabic"),
    [LegalTranslateLanguageTypeEnum.Ind]: t("common.indonesian"),
    [LegalTranslateLanguageTypeEnum.Cmn]: t("common.chinese2"),
    [LegalTranslateLanguageTypeEnum.Zho]: t("common.chinese3"),
    [LegalTranslateLanguageTypeEnum.Eng]: t("common.english"),
    [LegalTranslateLanguageTypeEnum.Kor]: t("common.korean"),
    [LegalTranslateLanguageTypeEnum.Jpn]: t("common.japanese"),
    [LegalTranslateLanguageTypeEnum.Fra]: t("common.french"),
    [LegalTranslateLanguageTypeEnum.Spa]: t("common.spanish"),
    [LegalTranslateLanguageTypeEnum.Deu]: t("common.german"),
    [LegalTranslateLanguageTypeEnum.Vie]: t("common.vietnamese"),
    [LegalTranslateLanguageTypeEnum.Por]: t("common.portuguese"),
    [LegalTranslateLanguageTypeEnum.Mya]: t("common.burmese"),
    [LegalTranslateLanguageTypeEnum.Khm]: t("common.khmer"),
    [LegalTranslateLanguageTypeEnum.Tha]: t("common.thai"),
    [LegalTranslateLanguageTypeEnum.Mon]: t("common.mongolian"),
    [LegalTranslateLanguageTypeEnum.Msa]: t("common.malay"),
  }
}

export const getPatentLanguageMenu = (
  t: TFunction
): Record<PatentTranslateLanguageTypeEnum, string> => {
  return {
    [PatentTranslateLanguageTypeEnum.Auto]: t("mt.detect"),
    [PatentTranslateLanguageTypeEnum.Ara]: t("common.arabic"),
    [PatentTranslateLanguageTypeEnum.Cmn]: t("common.chinese2"),
    [PatentTranslateLanguageTypeEnum.Zho]: t("common.chinese3"),
    [PatentTranslateLanguageTypeEnum.Eng]: t("common.english"),
    [PatentTranslateLanguageTypeEnum.Kor]: t("common.korean"),
    [PatentTranslateLanguageTypeEnum.Ind]: t("common.indonesian"),
    [PatentTranslateLanguageTypeEnum.Jpn]: t("common.japanese"),
    [PatentTranslateLanguageTypeEnum.Fra]: t("common.french"),
    [PatentTranslateLanguageTypeEnum.Spa]: t("common.spanish"),
    [PatentTranslateLanguageTypeEnum.Deu]: t("common.german"),
    [PatentTranslateLanguageTypeEnum.Vie]: t("common.vietnamese"),
  }
}

export const getKakaoLanguageMenu = (
  t: TFunction
): Record<KakaoTranslateLanguageTypeEnum, string> => {
  return {
    [KakaoTranslateLanguageTypeEnum.Auto]: t("mt.detect"),
    [KakaoTranslateLanguageTypeEnum.Eng]: t("common.english"),
    [KakaoTranslateLanguageTypeEnum.Kor]: t("common.korean"),
  }
}

export const MAX_CHARACTERS = 5_000
export const MAX_FILE_SIZE = 1024 * 1024 * 40 // 40MB
export const MAX_FILES = 5
export const MAX_PDF_PAGES = 100

export const isStoredDomainCode = (value: unknown): value is DomainTypeEnum =>
  typeof value === "string" && /^[a-z][a-z0-9_]{0,63}$/.test(value)

// Validates a (potentially stale) translation form loaded from localStorage
// against the enums and language-pair rules that the *current* code knows
// about. Any field whose value is no longer recognized falls back to the
// default. This protects against stale entries (e.g. a "medical" domain set
// on another branch that doesn't exist in this one).
export const validateStoredTranslationForm = (
  raw: unknown
): MachineTranslationForm => {
  const fallback = DEFAULT_TRANSLATION_FORM
  if (!raw || typeof raw !== "object") return fallback

  const candidate = raw as Partial<MachineTranslationForm>

  const validEngines = Object.values(TranslationEngineEnum) as string[]
  const validLanguage = (value: unknown): value is TranslateLanguageTypeEnum => typeof value === "string" && /^(auto|[a-z]{2,3}(?:-[a-z0-9]+)?)$/.test(value)

  const engine = validEngines.includes(candidate.engine as string)
    ? (candidate.engine as TranslationEngineEnum)
    : fallback.engine

  const sourceLanguage = validLanguage(candidate.sourceLanguage)
    ? (candidate.sourceLanguage as TranslateLanguageTypeEnum)
    : fallback.sourceLanguage

  let targetLanguage = validLanguage(candidate.targetLanguage)
    ? (candidate.targetLanguage as TranslateLanguageTypeEnum)
    : fallback.targetLanguage

  // Auto is only valid as source.
  if (targetLanguage === TranslateLanguageTypeEnum.Auto) {
    targetLanguage = TranslateLanguageTypeEnum.En
  }

  // Same source and target is invalid; fall back to a sensible pair.
  if (sourceLanguage === targetLanguage) {
    targetLanguage =
      sourceLanguage === TranslateLanguageTypeEnum.En
        ? TranslateLanguageTypeEnum.Ko
        : TranslateLanguageTypeEnum.En
  }

  const domain =
    isStoredDomainCode(candidate.domain)
      ? (candidate.domain as DomainTypeEnum)
      : undefined

  // Submission checks the live catalogue without changing the selected languages.

  return {
    ...fallback,
    engine,
    sourceLanguage,
    targetLanguage,
    ...(domain && { domain }),
  }
}

export const DEFAULT_TRANSLATION_FORM: MachineTranslationForm = {
  inputPath: "",
  sourceLanguage: TranslateLanguageTypeEnum.Auto,
  targetLanguage: TranslateLanguageTypeEnum.En,
  sourceContent: "",
  engine: TranslationEngineEnum.BeringAI,
}

export const TRANSLATION_ENGINE_OPTIONS = [
  {
    value: TranslationEngineEnum.BeringAI,
    label: "BeringAI",
    icon: <Logo fill="currentColor" className="text-key-color" width="14" height="14" />,
    descriptionKey: "mt.engineBeringAIDesc" as const,
    selectedClassName: "text-key-color",
    requiresPaidPlan: false,
  },
  {
    value: TranslationEngineEnum.DeepL,
    label: "DeepL",
    icon: <Deepl width="14" height="14" />,
    descriptionKey: "mt.engineDeepLDesc" as const,
    requiresPaidPlan: true,
  },
]

// ISO codes for languages DeepL does not support. Same string codes are reused
// across TranslateLanguageTypeEnum / LegalTranslateLanguageTypeEnum, so a
// string check works across all language enums.
export const DEEPL_UNSUPPORTED_LANGUAGE_CODES: string[] = [
  TranslateLanguageTypeEnum.Km, // Khmer (Cambodian)
  TranslateLanguageTypeEnum.Bo, // Tibetan
  TranslateLanguageTypeEnum.Ug, // Uyghur
]

export const isLanguageSupportedByEngine = (
  lang: string,
  engine: TranslationEngineEnum
): boolean => {
  if (engine !== TranslationEngineEnum.DeepL) return true
  return !DEEPL_UNSUPPORTED_LANGUAGE_CODES.includes(lang)
}

export const DEFAULT_FILE_QUEUE: FileQueue[] = [
  {
    id: "",
    status: TranslationStatusEnum.Ready,
    sourceTitle: "",
    errorCode: null,
    sourceLanguage: TranslateLanguageTypeEnum.Auto,
    targetLanguage: TranslateLanguageTypeEnum.En,
    domain: DomainTypeEnum.Business,
    jobId: "",
    isDeleted: false,
    inputPath: "",
    smartGlossaryExtracted: false,
  },
]

export const getEnglishMenu = (): Record<TranslateLanguageTypeEnum, string> => {
  return {
    [TranslateLanguageTypeEnum.Auto]: "Detect Language",
    [TranslateLanguageTypeEnum.Zh]: "Chinese (Simplified)",
    [TranslateLanguageTypeEnum.En]: "English",
    [TranslateLanguageTypeEnum.Fr]: "French",
    [TranslateLanguageTypeEnum.Pt]: "Portuguese",
    [TranslateLanguageTypeEnum.Es]: "Spanish",
    [TranslateLanguageTypeEnum.Ja]: "Japanese",
    [TranslateLanguageTypeEnum.Tr]: "Turkish",
    [TranslateLanguageTypeEnum.Ru]: "Russian",
    [TranslateLanguageTypeEnum.Ar]: "Arabic",
    [TranslateLanguageTypeEnum.Ko]: "Korean",
    [TranslateLanguageTypeEnum.Th]: "Thai",
    [TranslateLanguageTypeEnum.It]: "Italian",
    [TranslateLanguageTypeEnum.De]: "German",
    [TranslateLanguageTypeEnum.Vi]: "Vietnamese",
    [TranslateLanguageTypeEnum.Ms]: "Malay",
    [TranslateLanguageTypeEnum.Id]: "Indonesian",
    [TranslateLanguageTypeEnum.Tl]: "Filipino",
    [TranslateLanguageTypeEnum.Hi]: "Hindi",
    [TranslateLanguageTypeEnum.ZhHant]: "Chinese (Traditional)",
    [TranslateLanguageTypeEnum.Pl]: "Polish",
    [TranslateLanguageTypeEnum.Cs]: "Czech",
    [TranslateLanguageTypeEnum.Nl]: "Dutch",
    [TranslateLanguageTypeEnum.Km]: "Khmer",
    [TranslateLanguageTypeEnum.My]: "Burmese",
    [TranslateLanguageTypeEnum.Fa]: "Persian",
    [TranslateLanguageTypeEnum.Gu]: "Gujarati",
    [TranslateLanguageTypeEnum.Ur]: "Urdu",
    [TranslateLanguageTypeEnum.Te]: "Telugu",
    [TranslateLanguageTypeEnum.Mr]: "Marathi",
    [TranslateLanguageTypeEnum.He]: "Hebrew",
    [TranslateLanguageTypeEnum.Bn]: "Bengali",
    [TranslateLanguageTypeEnum.Ta]: "Tamil",
    [TranslateLanguageTypeEnum.Uk]: "Ukrainian",
    [TranslateLanguageTypeEnum.Bo]: "Tibetan",
    [TranslateLanguageTypeEnum.Kk]: "Kazakh",
    [TranslateLanguageTypeEnum.Mn]: "Mongolian",
    [TranslateLanguageTypeEnum.Ug]: "Uyghur",
    [TranslateLanguageTypeEnum.Yue]: "Cantonese",
  }
}

export const bannerContents = (t: TFunction) => {
  return [
    {
      value: <img src={img1} alt="img1" />,
      description: t("mt.uploadDocuments"),
    },
    {
      value: <img src={img2} alt="img2" />,
      description: t("mt.approveOrder"),
    },
    {
      value: <img src={img3} alt="img3" />,
      description: t("mt.expertReview2"),
    },
    {
      value: <img src={img4} alt="img4" />,
      description: t("mt.translationDelivery"),
    },
  ]
}

export const bannerDescriptionContents = (t: TFunction) => {
  return [
    {
      icon: <AcuteIcon fill="#4f00a7" />,
      title: t("common.speed"),
      value: "3X",
      description: t("mt.fasterTurnaroundTimes"),
    },
    {
      icon: <AccountBalanceWalletIcon fill="#4f00a7" />,
      title: t("mt.price"),
      value: "-40%",
      description: t("mt.significantCostReductionWithZeroCompromises"),
    },
    {
      icon: <TargetIcon fill="#4f00a7" />,
      title: t("common.accuracy"),
      value: "99%",
      description: t("mt.consistentQualityNoHumanError"),
    },
  ]
}

export const faqs = (t: TFunction) => {
  return [
    {
      title: t("mt.faq1Title"),
      content: (
        <Trans i18nKey="mt.faq1Description" components={{ br: <br /> }} />
      ),
    },
    {
      title: t("mt.faq2Title"),
      content: <Trans i18nKey="mt.faq2Description" components={{ a: <a /> }} />,
    },
    {
      title: t("mt.faq3Title"),
      content: t("mt.faq3Description"),
    },
    {
      title: t("mt.faq4Title"),
      content: t("mt.faq4Description"),
    },
    {
      title: t("mt.faq5Title"),
      content: t("mt.faq5Description"),
    },
    {
      title: t("mt.faq6Title"),
      content: t("mt.faq6Description"),
    },
  ]
}

export const SUPPORTED_LANGUAGES = [
  TranslateLanguageTypeEnum.En,
  TranslateLanguageTypeEnum.Ja,
  TranslateLanguageTypeEnum.Zh,
]

export const getTabButtons = (
  t: TFunction,
  enableDetection: boolean,
  isFileTranslationTab: boolean,
  isExpertReviews: boolean
  // isMediaTranslationTab: boolean
) => {
  return [
    {
      mode: TranslationModeEnum.Text,
      label: t("mt.text"),
      isActive: enableDetection,
      icon: () => (
        <Languages width={18} height={18} className="text-key-color" />
      ),
      className: "rounded-e-none hover:text-key-color/80 text-key-color",
      additionalStyles: "border-r-0",
    },
    {
      mode: TranslationModeEnum.File,
      label: t("mt.fileUpload"),
      isActive: isFileTranslationTab,
      icon: () => (
        <DocumentTranslationIcon
          width={18}
          height={18}
          fill="#4f00a7"
          className="group-hover:fill-key-color"
        />
      ),
      className: "rounded-none hover:text-key-color/80 text-key-color",
    },
    // {
    //   mode: TranslationModeEnum.Media,
    //   label: t("mt.media"),
    //   isActive: isMediaTranslationTab,
    //   icon: () => (
    //     <SmartDisplayIcon
    //       width={18}
    //       height={18}
    //       fill="#4f00a7"
    //       className="group-hover:fill-key-color"
    //     />
    //   ),
    //   className: "rounded-none hover:text-key-color/80 text-key-color",
    //   additionalStyles: "border-l-0",
    // },
    {
      mode: TranslationModeEnum.ExpertReviews,
      label: t("mt.expertTranslation"),
      isActive: isExpertReviews,
      icon: () => (
        <PremiumIcon
          width={18}
          height={18}
          fill="#4f00a7"
          className="group-hover:fill-key-color"
        />
      ),
      className: "rounded-s-none hover:text-key-color/80 text-key-color",
      additionalStyles: "border-l-0",
    },
  ]
}

const getGeneralDomainOptions = (
  t: TFunction,
  isTargetLanguage: boolean,
  enableDetection: boolean = true,
  excludedLanguage?: TranslateLanguageTypeEnum,
  engine: TranslationEngineEnum = TranslationEngineEnum.BeringAI
) => {
  const languageMenu = getLanguageMenu(t)

  const baseLanguages = Object.values(TranslateLanguageTypeEnum).filter(
    (lang) =>
      lang !== TranslateLanguageTypeEnum.Auto && lang !== excludedLanguage
  )

  const supportedByEngine = baseLanguages.filter((lang) =>
    isLanguageSupportedByEngine(lang, engine)
  )
  const unsupportedByEngine = baseLanguages.filter(
    (lang) => !isLanguageSupportedByEngine(lang, engine)
  )

  if (!isTargetLanguage) {
    const sortedSupported = [...supportedByEngine].sort((a, b) =>
      languageMenu[a].localeCompare(languageMenu[b])
    )
    const sortedLanguages = enableDetection
      ? [TranslateLanguageTypeEnum.Auto, ...sortedSupported]
      : sortedSupported

    return (
      <>
        {sortedLanguages.map((languageType) => (
          <SelectItem key={languageType} value={languageType}>
            {languageMenu[languageType]}
          </SelectItem>
        ))}
        {unsupportedByEngine.length > 0 && (
          <>
            <SelectSeparator />
            <NotSupportedGroup
              languageTypes={unsupportedByEngine}
              languageMenu={languageMenu}
              t={t}
            />
          </>
        )}
      </>
    )
  }

  // 타겟 언어에서는 Auto 옵션 제외, 그리고 source와 같은 언어 제외
  return (
    <>
      {createSelectItems(supportedByEngine, languageMenu)}
      {unsupportedByEngine.length > 0 && (
        <>
          <SelectSeparator />
          <NotSupportedGroup
            languageTypes={unsupportedByEngine}
            languageMenu={languageMenu}
            t={t}
          />
        </>
      )}
      <SelectSeparator />
    </>
  )
}

// 법률 도메인 지원 언어 쌍 설정 (single source of truth)
// key: source language, value: supported target languages
const LEGAL_SUPPORTED_TARGETS: Partial<
  Record<LegalTranslateLanguageTypeEnum, LegalTranslateLanguageTypeEnum[]>
> = {
  [LegalTranslateLanguageTypeEnum.Eng]: Object.values(
    LegalTranslateLanguageTypeEnum
  ).filter((lang) => lang !== LegalTranslateLanguageTypeEnum.Auto && lang !== LegalTranslateLanguageTypeEnum.Eng),
  [LegalTranslateLanguageTypeEnum.Kor]: [
    LegalTranslateLanguageTypeEnum.Eng,
    LegalTranslateLanguageTypeEnum.Cmn,
    LegalTranslateLanguageTypeEnum.Zho,
    LegalTranslateLanguageTypeEnum.Jpn,
    LegalTranslateLanguageTypeEnum.Mya,
    LegalTranslateLanguageTypeEnum.Khm,
    LegalTranslateLanguageTypeEnum.Ind,
    LegalTranslateLanguageTypeEnum.Tha,
    LegalTranslateLanguageTypeEnum.Mon,
    LegalTranslateLanguageTypeEnum.Msa,
    LegalTranslateLanguageTypeEnum.Vie,
  ],
  [LegalTranslateLanguageTypeEnum.Cmn]: [
    LegalTranslateLanguageTypeEnum.Eng,
    LegalTranslateLanguageTypeEnum.Kor,
    LegalTranslateLanguageTypeEnum.Jpn,
  ],
  [LegalTranslateLanguageTypeEnum.Zho]: [
    LegalTranslateLanguageTypeEnum.Eng,
    LegalTranslateLanguageTypeEnum.Kor,
    LegalTranslateLanguageTypeEnum.Jpn,
  ],
  [LegalTranslateLanguageTypeEnum.Jpn]: [
    LegalTranslateLanguageTypeEnum.Eng,
    LegalTranslateLanguageTypeEnum.Kor,
    LegalTranslateLanguageTypeEnum.Cmn,
    LegalTranslateLanguageTypeEnum.Zho,
  ],
  [LegalTranslateLanguageTypeEnum.Mya]: [
    LegalTranslateLanguageTypeEnum.Kor,
    LegalTranslateLanguageTypeEnum.Eng,
  ],
  [LegalTranslateLanguageTypeEnum.Khm]: [
    LegalTranslateLanguageTypeEnum.Kor,
    LegalTranslateLanguageTypeEnum.Eng,
  ],
  [LegalTranslateLanguageTypeEnum.Ind]: [
    LegalTranslateLanguageTypeEnum.Kor,
    LegalTranslateLanguageTypeEnum.Eng,
  ],
  [LegalTranslateLanguageTypeEnum.Tha]: [
    LegalTranslateLanguageTypeEnum.Kor,
    LegalTranslateLanguageTypeEnum.Eng,
  ],
  [LegalTranslateLanguageTypeEnum.Mon]: [
    LegalTranslateLanguageTypeEnum.Kor,
    LegalTranslateLanguageTypeEnum.Eng,
  ],
  [LegalTranslateLanguageTypeEnum.Msa]: [
    LegalTranslateLanguageTypeEnum.Kor,
    LegalTranslateLanguageTypeEnum.Eng,
  ],
  [LegalTranslateLanguageTypeEnum.Vie]: [
    LegalTranslateLanguageTypeEnum.Kor,
    LegalTranslateLanguageTypeEnum.Eng,
  ],
}

export const getLegalSupportedTargets = (
  source: LegalTranslateLanguageTypeEnum
): LegalTranslateLanguageTypeEnum[] => {
  return LEGAL_SUPPORTED_TARGETS[source] ?? [LegalTranslateLanguageTypeEnum.Eng]
}

const getLegalDomainOptions = (
  t: TFunction,
  isTargetLanguage: boolean,
  sourceLanguage: LegalTranslateLanguageTypeEnum,
  enableDetection: boolean = true,
  engine: TranslationEngineEnum = TranslationEngineEnum.BeringAI
) => {
  const legalLanguageMenu = getLegalLanguageMenu(t)

  if (!isTargetLanguage) {
    const baseLanguages = Object.values(LegalTranslateLanguageTypeEnum).filter(
      (lang) => lang !== LegalTranslateLanguageTypeEnum.Auto
    )

    const supportedByEngine = baseLanguages.filter((lang) =>
      isLanguageSupportedByEngine(lang, engine)
    )
    const unsupportedByEngine = baseLanguages.filter(
      (lang) => !isLanguageSupportedByEngine(lang, engine)
    )

    const sortedSupported = [...supportedByEngine].sort((a, b) =>
      legalLanguageMenu[a].localeCompare(legalLanguageMenu[b])
    )
    const sortedLanguages = enableDetection
      ? [LegalTranslateLanguageTypeEnum.Auto, ...sortedSupported]
      : sortedSupported

    return (
      <>
        {sortedLanguages.map((languageType) => (
          <SelectItem key={languageType} value={languageType}>
            {legalLanguageMenu[languageType]}
          </SelectItem>
        ))}
        {unsupportedByEngine.length > 0 && (
          <>
            <SelectSeparator />
            <NotSupportedGroup
              languageTypes={unsupportedByEngine}
              languageMenu={legalLanguageMenu}
              t={t}
            />
          </>
        )}
      </>
    )
  }

  const allSupportedTargets = getLegalSupportedTargets(sourceLanguage)
  // Engine-supported subset for the "supported" group
  const supportedTargets = allSupportedTargets.filter((lang) =>
    isLanguageSupportedByEngine(lang, engine)
  )
  // Targets that are domain-supported but engine-unsupported (e.g. Khmer w/ DeepL)
  const engineUnsupportedFromDomain = allSupportedTargets.filter(
    (lang) => !isLanguageSupportedByEngine(lang, engine)
  )

  // 중국어 번체: 한국어, 영어 순으로 우선 정렬
  const displayTargets =
    sourceLanguage === LegalTranslateLanguageTypeEnum.Zho
      ? sortLanguagesByPriority(
          supportedTargets,
          [
            LegalTranslateLanguageTypeEnum.Kor,
            LegalTranslateLanguageTypeEnum.Eng,
          ],
          legalLanguageMenu
        )
      : supportedTargets

  const domainUnsupportedTargets = Object.values(
    LegalTranslateLanguageTypeEnum
  ).filter(
    (lang) =>
      !allSupportedTargets.includes(lang) &&
      lang !== LegalTranslateLanguageTypeEnum.Auto &&
      lang !== sourceLanguage
  )

  const unsupportedTargets = [
    ...engineUnsupportedFromDomain,
    ...domainUnsupportedTargets,
  ]

  // 영어 소스이고 엔진 제한도 없으면 미지원 그룹 불필요
  if (
    sourceLanguage === LegalTranslateLanguageTypeEnum.Eng &&
    unsupportedTargets.length === 0
  ) {
    return (
      <>
        {createSelectItems(displayTargets, legalLanguageMenu)}
        <SelectSeparator />
      </>
    )
  }

  return (
    <>
      {createSelectItems(displayTargets, legalLanguageMenu)}
      {unsupportedTargets.length > 0 && (
        <>
          <SelectSeparator />
          <NotSupportedGroup
            languageTypes={unsupportedTargets}
            languageMenu={legalLanguageMenu}
            t={t}
          />
        </>
      )}
    </>
  )
}

// 특허 도메인 양방향 지원 언어 (single source of truth)
const PATENT_SUPPORTED_LANGUAGES: PatentTranslateLanguageTypeEnum[] = [
  PatentTranslateLanguageTypeEnum.Eng,
  PatentTranslateLanguageTypeEnum.Jpn,
  PatentTranslateLanguageTypeEnum.Kor,
  PatentTranslateLanguageTypeEnum.Cmn,
  PatentTranslateLanguageTypeEnum.Zho,
  PatentTranslateLanguageTypeEnum.Deu,
  PatentTranslateLanguageTypeEnum.Spa,
  PatentTranslateLanguageTypeEnum.Fra,
]

export const isPatentLanguageSupported = (
  lang: PatentTranslateLanguageTypeEnum
): boolean => PATENT_SUPPORTED_LANGUAGES.includes(lang)

const getPatentDomainOptions = (
  t: TFunction,
  isTargetLanguage: boolean,
  sourceLanguage: PatentTranslateLanguageTypeEnum,
  enableDetection: boolean = true
) => {
  const patentLanguageMenu = getPatentLanguageMenu(t)

  if (!isTargetLanguage) {
    const sortedLanguages = [...PATENT_SUPPORTED_LANGUAGES].sort((a, b) =>
      patentLanguageMenu[a].localeCompare(patentLanguageMenu[b])
    )

    const languages = enableDetection
      ? [PatentTranslateLanguageTypeEnum.Auto, ...sortedLanguages]
      : sortedLanguages

    return languages.map((languageType) => (
      <SelectItem key={languageType} value={languageType}>
        {patentLanguageMenu[languageType]}
      </SelectItem>
    ))
  }

  const isSourceSupported = PATENT_SUPPORTED_LANGUAGES.includes(sourceLanguage)

  const supportedTargets = isSourceSupported
    ? PATENT_SUPPORTED_LANGUAGES.filter((lang) => lang !== sourceLanguage)
    : [PatentTranslateLanguageTypeEnum.Eng]

  const unsupportedTargets = Object.values(
    PatentTranslateLanguageTypeEnum
  ).filter(
    (lang) =>
      !supportedTargets.includes(lang) &&
      lang !== PatentTranslateLanguageTypeEnum.Auto &&
      lang !== sourceLanguage
  )

  return (
    <>
      {createSelectItems(supportedTargets, patentLanguageMenu)}
      {unsupportedTargets.length > 0 && (
        <>
          <SelectSeparator />
          <NotSupportedGroup
            languageTypes={unsupportedTargets}
            languageMenu={patentLanguageMenu}
            t={t}
          />
        </>
      )}
    </>
  )
}

// 임상 도메인: 한국어 <-> 영어만 지원
const MEDICAL_SUPPORTED_LANGUAGES: TranslateLanguageTypeEnum[] = [
  TranslateLanguageTypeEnum.Ko,
  TranslateLanguageTypeEnum.En,
]

export const isMedicalLanguageSupported = (
  lang: TranslateLanguageTypeEnum
): boolean => MEDICAL_SUPPORTED_LANGUAGES.includes(lang)

const getMedicalDomainOptions = (
  t: TFunction,
  isTargetLanguage: boolean,
  sourceLanguage: TranslateLanguageTypeEnum
) => {
  const languageMenu = getLanguageMenu(t)

  if (!isTargetLanguage) {
    const sortedLanguages = [...MEDICAL_SUPPORTED_LANGUAGES].sort((a, b) =>
      languageMenu[a].localeCompare(languageMenu[b])
    )

    return sortedLanguages.map((languageType) => (
      <SelectItem key={languageType} value={languageType}>
        {languageMenu[languageType]}
      </SelectItem>
    ))
  }

  const supportedTargets = MEDICAL_SUPPORTED_LANGUAGES.filter(
    (lang) => lang !== sourceLanguage
  )

  const unsupportedTargets = Object.values(TranslateLanguageTypeEnum).filter(
    (lang) =>
      !supportedTargets.includes(lang) &&
      lang !== TranslateLanguageTypeEnum.Auto &&
      lang !== sourceLanguage
  )

  return (
    <>
      {createSelectItems(supportedTargets, languageMenu)}
      {unsupportedTargets.length > 0 && (
        <>
          <SelectSeparator />
          <NotSupportedGroup
            languageTypes={unsupportedTargets}
            languageMenu={languageMenu}
            t={t}
          />
        </>
      )}
    </>
  )
}

// 카카오 도메인 지원 언어 (Korean ↔ English only)
const KAKAO_SUPPORTED_LANGUAGES: KakaoTranslateLanguageTypeEnum[] = [
  KakaoTranslateLanguageTypeEnum.Eng,
  KakaoTranslateLanguageTypeEnum.Kor,
]

const getKakaoDomainOptions = (
  t: TFunction,
  isTargetLanguage: boolean,
  sourceLanguage: KakaoTranslateLanguageTypeEnum,
  enableDetection: boolean = true
) => {
  const kakaoLanguageMenu = getKakaoLanguageMenu(t)

  if (!isTargetLanguage) {
    const sortedLanguages = [...KAKAO_SUPPORTED_LANGUAGES].sort((a, b) =>
      kakaoLanguageMenu[a].localeCompare(kakaoLanguageMenu[b])
    )

    const languages = enableDetection
      ? [KakaoTranslateLanguageTypeEnum.Auto, ...sortedLanguages]
      : sortedLanguages

    return languages.map((languageType) => (
      <SelectItem key={languageType} value={languageType}>
        {kakaoLanguageMenu[languageType]}
      </SelectItem>
    ))
  }

  const supportedTargets = KAKAO_SUPPORTED_LANGUAGES.filter(
    (lang) => lang !== sourceLanguage
  )

  return (
    <>
      {createSelectItems(supportedTargets, kakaoLanguageMenu)}
    </>
  )
}

export const getLanguageOptions = (
  t: TFunction,
  domain: DomainTypeEnum,
  isTargetLanguage: boolean = false,
  sourceLanguage?:
    | TranslateLanguageTypeEnum
    | LegalTranslateLanguageTypeEnum
    | PatentTranslateLanguageTypeEnum
    | KakaoTranslateLanguageTypeEnum,
  enableDetection: boolean = true,
  // The language currently selected on the *opposite* side. The dropdown will
  // omit this language so the user cannot select an identical source/target
  // pair.
  oppositeLanguage?:
    | TranslateLanguageTypeEnum
    | LegalTranslateLanguageTypeEnum
    | PatentTranslateLanguageTypeEnum
    | KakaoTranslateLanguageTypeEnum,
  engine: TranslationEngineEnum = TranslationEngineEnum.BeringAI
) => {
  switch (domain) {
    case DomainTypeEnum.General:
      return getGeneralDomainOptions(
        t,
        isTargetLanguage,
        enableDetection,
        oppositeLanguage as TranslateLanguageTypeEnum | undefined,
        engine
      )
    case DomainTypeEnum.Legal:
      return getLegalDomainOptions(
        t,
        isTargetLanguage,
        sourceLanguage as LegalTranslateLanguageTypeEnum,
        enableDetection,
        engine
      )
    case DomainTypeEnum.Patent:
      return getPatentDomainOptions(
        t,
        isTargetLanguage,
        sourceLanguage as PatentTranslateLanguageTypeEnum,
        enableDetection
      )
    case DomainTypeEnum.Medical:
      return getMedicalDomainOptions(
        t,
        isTargetLanguage,
        sourceLanguage as TranslateLanguageTypeEnum
      )
    case DomainTypeEnum.Kakao:
      return getKakaoDomainOptions(
        t,
        isTargetLanguage,
        sourceLanguage as KakaoTranslateLanguageTypeEnum,
        enableDetection
      )
  }
}

export const ERROR_MESSAGES = {
  INVALID_LANGUAGE_PAIR: "MtError(InvalidLanguagePair)",
  UNIT_COUNT_LIMITED: "UnitCountLimited",
}

// 언어 쌍이 지원되는지 확인하는 함수
export const isLanguagePairSupported = (
  sourceLanguage:
    | TranslateLanguageTypeEnum
    | LegalTranslateLanguageTypeEnum
    | PatentTranslateLanguageTypeEnum
    | KakaoTranslateLanguageTypeEnum,
  targetLanguage:
    | TranslateLanguageTypeEnum
    | LegalTranslateLanguageTypeEnum
    | PatentTranslateLanguageTypeEnum
    | KakaoTranslateLanguageTypeEnum,
  domain: DomainTypeEnum,
  engine: TranslationEngineEnum = TranslationEngineEnum.BeringAI
): boolean => {
  // Auto는 source에서만 가능
  if (
    targetLanguage === TranslateLanguageTypeEnum.Auto ||
    targetLanguage === LegalTranslateLanguageTypeEnum.Auto ||
    targetLanguage === PatentTranslateLanguageTypeEnum.Auto ||
    targetLanguage === KakaoTranslateLanguageTypeEnum.Auto
  ) {
    return false
  }

  // 같은 언어는 불가능
  if (sourceLanguage === targetLanguage) {
    return false
  }

  // 엔진이 source/target 언어를 지원하지 않는 경우 (예: DeepL + Khmer/Tibetan/Uyghur)
  if (
    !isLanguageSupportedByEngine(sourceLanguage, engine) ||
    !isLanguageSupportedByEngine(targetLanguage, engine)
  ) {
    return false
  }

  switch (domain) {
    case DomainTypeEnum.Legal: {
      const source = sourceLanguage as LegalTranslateLanguageTypeEnum
      const target = targetLanguage as LegalTranslateLanguageTypeEnum
      return getLegalSupportedTargets(source).includes(target)
    }

    case DomainTypeEnum.Patent: {
      const source = sourceLanguage as PatentTranslateLanguageTypeEnum
      const target = targetLanguage as PatentTranslateLanguageTypeEnum
      return (
        isPatentLanguageSupported(source) &&
        isPatentLanguageSupported(target)
      )
    }

    case DomainTypeEnum.Medical: {
      const source = sourceLanguage as TranslateLanguageTypeEnum
      const target = targetLanguage as TranslateLanguageTypeEnum
      return (
        isMedicalLanguageSupported(source) &&
        isMedicalLanguageSupported(target)
      )
    }

    case DomainTypeEnum.Kakao: {
      const source = sourceLanguage as KakaoTranslateLanguageTypeEnum
      const target = targetLanguage as KakaoTranslateLanguageTypeEnum
      return (
        KAKAO_SUPPORTED_LANGUAGES.includes(source) &&
        KAKAO_SUPPORTED_LANGUAGES.includes(target)
      )
    }

    default:
      // General 도메인 - 모든 언어 쌍 지원 (Auto 제외)
      return true
  }
}

export const loadingMessages = (domain: DomainTypeEnum, t: TFunction) => {
  switch (domain) {
    case DomainTypeEnum.Legal:
      return [
        t("mt.legalLoadingMessage1"),
        t("mt.legalLoadingMessage2"),
        t("mt.legalLoadingMessage3"),
        t("mt.legalLoadingMessage4"),
      ]
    case DomainTypeEnum.Patent:
      return [
        t("mt.patentLoadingMessage1"),
        t("mt.patentLoadingMessage2"),
        t("mt.patentLoadingMessage3"),
        t("mt.patentLoadingMessage4"),
      ]
    case DomainTypeEnum.Medical:
      return [
        t("mt.medicalLoadingMessage1"),
        t("mt.medicalLoadingMessage2"),
        t("mt.medicalLoadingMessage3"),
        t("mt.medicalLoadingMessage4"),
      ]
    case DomainTypeEnum.General:
    default:
      return [
        t("mt.generalLoadingMessage1"),
        t("mt.generalLoadingMessage2"),
        t("mt.generalLoadingMessage3"),
        t("mt.generalLoadingMessage4"),
      ]
  }
}

export const sampleTranslationData = {
  sourceLanguage: "en",
  targetLanguage: "ko",
  sourceTitle: "test.pdf",
  jobId: 100,
  data: [
    {
      segmentId: 0,
      sourceText: "ABBYY Software License Agreement",
      translatedText: "ABBYY 소프트웨어 라이선스 계약서",
    },
    {
      segmentId: 1,
      sourceText: "Reference Number: 15909.SG.2025",
      translatedText: "참조 번호: 15909.SG.2025",
    },
    {
      segmentId: 2,
      sourceText:
        'This Software License Agreement (the "Agreement"), effective as of August 15, 2025 (the "Effective Date"), is by and between ABBYY Singapore PTE. LTD., with offices located at 8 Marina View #43-038 Asia Square Tower 1, Singapore, 018960, Singapore ("ABBYY") and Bering Lab, Inc, with offices located at Unit 804, 21, Baekbeom-ro 31-gil, Mapo-gu, Seoul, Korea ("Licensee"). Licensor and Licensee may be referred to herein collectively as the "Parties" or individually as a "Party."',
      translatedText:
        '본 소프트웨어 라이선스 계약서(이하 "계약서")는 2025년 8월 15일(이하 "효력발생일")부터 효력을 발생하며, 싱가포르 018960 8 Marina View #43-038 Asia Square Tower 1에 사무소를 둔 ABBYY Singapore PTE. LTD.(이하 "ABBYY")와 대한민국 서울특별시 마포구 백범로31길 21 Unit 804에 사무소를 둔 Bering Lab, Inc.(이하 "라이선시") 간에 체결된다. 라이선서와 라이선시를 본 계약서에서 총칭하여 "당사자들" 또는 개별적으로 "당사자"라 한다.',
    },
    {
      segmentId: 3,
      sourceText:
        "WHEREAS, Licensor desires to license the Software to Licensee; and",
      translatedText:
        "라이선서는 라이선시에게 소프트웨어를 라이선스하고자 하며",
    },
    {
      segmentId: 4,
      sourceText:
        "WHEREAS, Licensee desires to obtain a license to use the Software for Licensed Use, subject to the terms and conditions of this Agreement.",
      translatedText:
        "라이선시는 본 계약의 조건에 따라 라이선스 사용을 위해 소프트웨어 사용 라이선스를 취득하고자 한다.",
    },
    {
      segmentId: 5,
      sourceText:
        "NOW, THEREFORE, in consideration of the mutual covenants, terms, and conditions set forth herein, and for other good and valuable consideration, the receipt and sufficiency of which are hereby acknowledged, the Parties agree as follows:",
      translatedText:
        "이에, 본 계약에 명시된 상호 확약, 조건 및 기타 유효하고 가치 있는 대가를 고려하여, 그 수령 및 충분성을 인정하며, 당사자들은 다음과 같이 합의한다:",
    },
    {
      segmentId: 6,
      sourceText: "1. Definitions",
      translatedText: "1. 정의",
    },
    {
      segmentId: 7,
      sourceText:
        "Unless otherwise defined in Product Specific Terms with respect to certain ABBYY Software the capitalized terms in this Agreement shall have the following meaning:",
      translatedText:
        "특정 ABBYY 소프트웨어와 관련하여 제품별 조건에서 달리 정의되지 않는 한, 본 계약의 대문자로 표기된 용어는 다음과 같은 의미를 가진다:",
    },
    {
      segmentId: 8,
      sourceText:
        "“ABBYY Software” means a software product referred to in the Quote, as further described in the Product Specific Terms.",
      translatedText:
        '"ABBYY 소프트웨어"는 견적서에 언급된 소프트웨어 제품을 의미하며, 제품별 조건에서 추가로 기술된다.',
    },
    {
      segmentId: 9,
      sourceText:
        '“Affiliate” means any corporation, company or other legal entity that Controls, is under the Control of, or under common Control with one of the Parties to this Agreement, but such entity constitutes an Affiliate only so long as such Control exists. "Control" and its variants "Controlled by" or "under common Control with" means legal, beneficial or equitable ownership of more than fifty percent (50%) of outstanding voting securities or partnership interests of the entity, or otherwise power to elect the directors, or direct or cause the direction of the entity\'s management and policies, whether by voting power, contract, or otherwise.',
      translatedText:
        '"계열사"는 본 계약 당사자 중 하나를 지배하거나, 그 지배를 받거나, 그와 공동지배관계에 있는 법인, 회사 또는 기타 법적 실체를 의미하되, 해당 실체는 그러한 지배관계가 존재하는 동안에만 계열사를 구성한다. "지배" 및 그 변형어인 "지배를 받는" 또는 "공동지배관계에 있는"은 해당 실체의 발행 의결권 증권 또는 파트너십 지분의 50%를 초과하는 법적, 수익적 또는 형평적 소유권을 의미하거나, 달리 이사를 선출하거나 의결권, 계약 또는 기타 방법으로 해당 실체의 경영 및 정책을 지시하거나 지시하게 할 권한을 의미한다.',
    },
    {
      segmentId: 10,
      sourceText:
        "“Application” means the software application specified and described in Appendix A developed by the Licensee and containing, integrated for use with or bundled with, ABBYY Software. The intellectual property rights to Application, excluding ABBYY Software component, and excluding any derivative works of or modifications to ABBYY Software, are owned by the Licensee.",
      translatedText:
        '"애플리케이션"은 라이선시가 개발하고 ABBYY 소프트웨어를 포함하거나, 이와 함께 사용하기 위해 통합되거나 번들로 제공되는 부록 A에서 명시되고 기술된 소프트웨어 애플리케이션을 의미한다. ABBYY 소프트웨어 구성요소 및 ABBYY 소프트웨어의 파생저작물 또는 수정물을 제외한 애플리케이션에 대한 지식재산권은 라이선시가 소유한다.',
    },
    {
      segmentId: 11,
      sourceText: "“Approved Source” means ABBYY or a Partner.",
      translatedText: '"승인된 출처"는 ABBYY 또는 파트너를 의미한다.',
    },
    {
      segmentId: 12,
      sourceText:
        "“Authorized Users” means Licensee and its Affiliates’ employees, representatives, consultants, contractors or agents whom Licensee permits to access and use the ABBYY Software on Licensee’s behalf.",
      translatedText:
        '"수권사용자"란 라이선시가 라이선시를 대신하여 ABBYY 소프트웨어에 접근하고 사용하도록 허용한 라이선시 및 그 계열사의 직원, 대표자, 컨설턴트, 계약자 또는 대리인을 의미한다.',
    },
    {
      segmentId: 13,
      sourceText:
        "“BPO Use” means the use of ABBYY Software to provide business process outsourcing (BPO) services to any third parties or to provide any other services where third parties are provided with the output of ABBYY Software. BPO Use does not cover ISV Distribution including use of ABBYY Software in external End User accessible Software-as-a-Service offerings.",
      translatedText:
        '"BPO 사용"이란 ABBYY 소프트웨어를 사용하여 제3자에게 비즈니스 프로세스 아웃소싱(BPO) 서비스를 제공하거나 제3자에게 ABBYY 소프트웨어의 출력물을 제공하는 기타 서비스를 제공하는 것을 의미한다. BPO 사용은 외부 최종사용자가 접근할 수 있는 소프트웨어-as-a-Service 제안에서 ABBYY 소프트웨어 사용을 포함한 ISV 유통을 포함하지 아니한다.',
    },
    {
      segmentId: 14,
      sourceText:
        "“Confidential Information” means non-public or proprietary information of the disclosing Party (“Disclosing Party”) obtained by the receiving Party (“Receiving Party”) in connection with this Agreement, which has been marked, designated or otherwise identified as confidential by either Party in writing or that ought reasonably to be considered as confidential (however it is disclosed or on whatever media it is stored) including information the disclosure of which would, or would be likely to, prejudice the commercial interests of any person, trade secrets, and know-how of either Party and all Personal Data. ABBYY's Confidential Information shall also include ABBYY Software and Documentation, including Evaluation Versions, Feedback, and Protection Keys, as well as information relating to ABBYY's technology, technical information, inventions, trade secrets, know-how, research, business methods and operations, processes, formulas, plans, strategies, customers, and customer lists, financial information and pricing, products (including without limitation ABBYY Software), software, training and support documentation, whether or not marked, designated, or otherwise identified as \"confidential\". Confidential Information does not include information that: (a) was rightfully known to the Receiving Party without restriction on use or disclosure prior to such information's being disclosed or made available to the Receiving Party in connection with this Agreement; (b) was or becomes generally known by the public other than by the Receiving Party's or any of its Representatives' noncompliance with this Agreement or other breach of a legal obligation; (c) was or is received by the Receiving Party on a non-confidential basis from a third party that, to the Receiving Party's knowledge, was not or is not, at the time of such receipt, under any obligation to maintain its confidentiality; or (d) was or is independently developed by the Receiving Party without reference to or use of any of the Disclosing Party's Confidential Information.",
      translatedText:
        '"비밀정보"란 본 계약과 관련하여 수령당사자("수령당사자")가 취득한 공개당사자("공개당사자")의 비공개 또는 독점 정보를 의미하며, 이는 일방 당사자에 의해 서면으로 비밀로 표시, 지정 또는 달리 확인되었거나 (공개 방식 또는 저장 매체에 관계없이) 합리적으로 비밀로 간주되어야 하는 정보를 포함한다. 여기에는 공개로 인해 다른 자의 상업적 이익이 침해되거나 침해될 가능성이 있는 정보, 영업비밀, 노하우 및 모든 개인정보가 포함된다. ABBYY의 비밀정보는 평가버전, 피드백 및 보호키를 포함한 ABBYY 소프트웨어 및 문서, 그리고 ABBYY의 기술, 기술정보, 발명, 영업비밀, 노하우, 연구, 사업방법 및 운영, 공정, 공식, 계획, 전략, 고객 및 고객목록, 재무정보 및 가격책정, 제품(ABBYY 소프트웨어를 포함하되 이에 한정되지 아니함), 소프트웨어, 교육 및 지원 문서와 관련된 정보를 포함하며, 이는 비밀로 표시되거나 지정되거나 달리 확인되는지 여부와 관계없이 포함된다. 비밀정보는 다음 중 어느 하나의 정보를 포함하지 아니한다: (a) 본 계약과 관련하여 해당 정보가 공개되거나 수령당사자에게 제공되기 전에 사용 또는 공개에 대한 제한 없이 수령당사자가 합법적으로 알고 있었던 정보; (b) 수령당사자 또는 그 대리인이 본 계약을 준수하지 아니하거나 기타 법적 의무를 위반한 경우를 제외하고 일반 대중에게 알려졌거나 알려지게 된 정보; (c) 수령당사자의 지식으로는 해당 수령 시점에 비밀유지 의무를 부담하지 아니하였거나 부담하지 아니하는 제3자로부터 수령당사자가 비비밀 기준으로 수령하였거나 수령하는 정보; 또는 (d) 공개당사자의 비밀정보를 참조하거나 사용하지 아니하고 수령당사자가 독립적으로 개발하였거나 개발하는 정보.',
    },
    {
      segmentId: 15,
      sourceText:
        "“Documentation” means the technical specifications, requirements and usage materials distributed by ABBYY specifying the functionalities and capabilities of ABBYY Software.",
      translatedText:
        '"문서"란 ABBYY 소프트웨어의 기능 및 능력을 명시하는 ABBYY가 배포한 기술사양, 요건 및 사용자료를 의미한다.',
    },
    {
      segmentId: 16,
      sourceText:
        "“End User” means any person or entity to whom Licensee, subject to the terms and conditions of this Agreement and only as permitted hereunder, sells, licenses, transfers, provides, or permits access to and use of the Application for such person’s or entity’s personal or internal business use, in each case subject to a license to the ABBYY Software bundled with or contained in the Application that is consistent with the minimum terms set in section 3.6.6 of this Agreement.",
      translatedText:
        '"최종 사용자"는 본 계약의 조건에 따라 본 계약에서 허용되는 경우에 한하여, 라이선시가 해당 개인 또는 법인의 개인적 또는 내부 사업 용도로 애플리케이션을 판매, 라이선스 부여, 이전, 제공하거나 접근 및 사용을 허용하는 개인 또는 법인을 의미하며, 각 경우 본 계약 제3.6.6조에 명시된 최소 조건에 부합하는 애플리케이션과 함께 제공되거나 포함된 ABBYY 소프트웨어에 대한 라이선스를 조건으로 한다.',
    },
    {
      segmentId: 17,
      sourceText:
        "“Internal Use” means the use of ABBYY Software for Licensee’s internal business operations and/or automation of Licensee’s internal processes. Internal Use does not include ISV Distribution or BPO Use.",
      translatedText:
        '"내부 사용"은 라이선시의 내부 사업 운영 및 라이선시의 내부 프로세스 자동화를 위한 ABBYY 소프트웨어의 사용을 의미한다. 내부 사용에는 ISV 유통 또는 BPO 사용이 포함되지 아니한다.',
    },
    {
      segmentId: 18,
      sourceText:
        "“ISV Distribution” means distribution of ABBYY Software to third-party End Users in conjunction, or integrated into and/or with a third-party or Licensee’s Application. “Licensed Use” means Internal Use, BPO Use and/or ISV Distribution as specified in Appendix A.",
      translatedText:
        '"ISV 유통"은 제3자 또는 라이선시의 애플리케이션과 함께 또는 이와 통합하여 제3자 최종 사용자에게 ABBYY 소프트웨어를 유통하는 것을 의미한다. "라이선스 사용"은 부록 A에 명시된 내부 사용, BPO 사용 및 ISV 유통을 의미한다.',
    },
    {
      segmentId: 19,
      sourceText:
        "“Quote” means a written document issued by ABBYY such as binding order form, agreement, quote or invoice specifying ABBYY Software to be licensed to Licensee, which may specify ABBYY Software add-ons, quantity, price, payment terms, Subscription Term, Volume Units and other applicable terms and conditions.",
      translatedText:
        '"견적서"는 라이선시에게 라이선스될 ABBYY 소프트웨어를 명시하는 구속력 있는 주문서, 계약서, 견적서 또는 청구서와 같이 ABBYY가 발행한 서면 문서를 의미하며, ABBYY 소프트웨어 추가 기능, 수량, 가격, 지급 조건, 구독 기간, 물량 단위 및 기타 적용 가능한 조건을 명시할 수 있다.',
    },
    {
      segmentId: 20,
      sourceText:
        "“Partner” means an entity authorized by ABBYY to market, promote and resell ABBYY Software, and/or to provide Protection Key to Licensee.",
      translatedText:
        '"파트너"란 ABBYY 소프트웨어를 마케팅, 홍보 및 재판매하거나 라이선시에게 보호 키를 제공하도록 ABBYY가 승인한 법인을 말한다.',
    },
    {
      segmentId: 21,
      sourceText:
        "“Personal Data” means any information that may be reasonably associated with, linked to, or used to individually identify a particular person or allow a particular person to be personally identified or contacted.",
      translatedText:
        '"개인정보"란 특정 개인과 합리적으로 연관되거나 연결되거나 특정 개인을 개별적으로 식별하거나 특정 개인이 개인적으로 식별되거나 연락받을 수 있도록 하는 데 사용될 수 있는 모든 정보를 말한다.',
    },
    {
      segmentId: 22,
      sourceText:
        "“Product Specific Terms” means terms specifically applicable to ABBYY Software appended hereto which constitute an integral part of this Agreement.",
      translatedText:
        '"제품별 조건"이란 본 계약에 첨부되어 본 계약의 불가분의 일부를 구성하는 ABBYY 소프트웨어에 특별히 적용되는 조건을 말한다.',
    },
    {
      segmentId: 23,
      sourceText:
        "“Protection Key” means a software key containing information about ABBYY Software and limitations on the scope of the Usage Rights.",
      translatedText:
        '"보호 키"란 ABBYY 소프트웨어에 관한 정보 및 사용권 범위의 제한사항을 포함하는 소프트웨어 키를 말한다.',
    },
    {
      segmentId: 24,
      sourceText:
        "“SM Terms” means ABBYY's terms and conditions for providing ABBYY Software support and maintenance services to Licensee, which are available at https://www.abbyy.com/sm-terms/ and constitute an integral part of this Agreement.",
      translatedText:
        '"SM 조건"은 라이선시에게 ABBYY 소프트웨어 지원 및 유지보수 서비스를 제공하기 위한 ABBYY의 조건을 의미하며, 이는 https://www.abbyy.com/sm-terms/에서 확인할 수 있고 본 계약의 불가분의 일부를 구성한다.',
    },
    {
      segmentId: 25,
      sourceText:
        "“Start Date” means: (a) the Subscription start date agreed in the applicable Quote or in documentation accompanying purchase from Approved Source (b) where no date is agreed the earliest of the date ABBYY Software and Protection Key are made available to Licensee.",
      translatedText:
        '"개시일"은 다음을 의미한다: (a) 해당 견적서 또는 승인된 공급원으로부터의 구매에 수반되는 문서에서 합의된 구독 개시일 (b) 합의된 날짜가 없는 경우 ABBYY 소프트웨어 및 보호 키가 라이선시에게 제공되는 날 중 가장 이른 날짜',
    },
    {
      segmentId: 26,
      sourceText:
        "“Subscription” means Licensee’s commitment to pay fee(s) to ABBYY for Usage Rights during the Subscription Term and in consideration of such payment, ABBYY’s commitment to grant Licensee such Usage Rights for the Subscription Term.",
      translatedText:
        '"구독"은 구독 기간 동안 사용권에 대해 라이선시가 ABBYY에 수수료를 지급할 약정 및 이러한 지급에 대한 대가로 ABBYY가 라이선시에게 구독 기간 동안 해당 사용권을 부여할 약정을 의미한다.',
    },
    {
      segmentId: 27,
      sourceText:
        "“Subscription Term” means the period for which ABBYY grants Licensee Usage Rights, commencing on the Start Date and continuing until expiration or termination of the Subscription. Unless otherwise set forth in the applicable Quote or otherwise agreed in writing by ABBYY, any Subscription Term for ABBYY Software will automatically renew in accordance with Subscription Terms.",
      translatedText:
        '"구독 기간"은 ABBYY가 라이선시에게 사용권을 부여하는 기간으로 개시일에 시작하여 구독의 만료 또는 해지 시까지를 의미한다. 해당 견적서에 달리 명시되거나 ABBYY가 서면으로 달리 합의하지 않는 한 ABBYY 소프트웨어의 구독 기간은 구독 조건에 따라 자동으로 갱신된다.',
    },
    {
      segmentId: 28,
      sourceText:
        "“Subscription Terms” means ABBYY’s Subscription Terms available at https://www.abbyy.com/legal/subscription-terms/ which are incorporated herein by reference and shall apply to Licensee’s Subscription.",
      translatedText:
        '"구독 조건"은 https://www.abbyy.com/legal/subscription-terms/에서 이용 가능한 ABBYY의 구독 조건을 의미하며, 이는 본 계약에 참조로 포함되어 라이선시의 구독에 적용된다.',
    },
    {
      segmentId: 29,
      sourceText: "“Term” shall mean Initial Term and Renewal Term(s).",
      translatedText: '"계약기간"은 최초 계약기간 및 갱신 계약기간을 의미한다.',
    },
    {
      segmentId: 30,
      sourceText: "“Territory” means the territory specified in Appendix A.",
      translatedText: '"지역"은 부록 A에 명시된 지역을 의미한다.',
    },
    {
      segmentId: 31,
      sourceText:
        "“Usage Rights” shall have the meaning prescribed in the section 2.1 below.",
      translatedText: '"사용권"은 아래 제2.1조에 규정된 의미를 갖는다.',
    },
    {
      segmentId: 32,
      sourceText:
        "“Volume Unit” means document transactions, documents, pages, events, projects or other types of information that may be processed using ABBYY Software.",
      translatedText:
        '"용량 단위"는 ABBYY 소프트웨어를 사용하여 처리될 수 있는 문서 거래, 문서, 페이지, 이벤트, 프로젝트 또는 기타 유형의 정보를 의미한다.',
    },
    {
      segmentId: 33,
      sourceText: "2. Subject Matter",
      translatedText: "2. 목적물",
    },
    {
      segmentId: 34,
      sourceText:
        "2.1 License and Right to Use. As consideration for Licensee’s payment of Subscription Fees and Licensee’s compliance with the terms and conditions of this Agreement, applicable Product Specific Terms, Subscription Terms, and Documentation, ABBYY grants Licensee a non-exclusive, non-transferable, revocable license to use ABBYY Software in the Territory during the Subscription Term for Licensed Use only (“Usage Rights”). The use of particular features, add-ons and/or custom components of ABBYY Software may be limited by Licensee’s Subscription scope and/or a written agreement between Licensee and an Approved Source and/or the documentation accompanying the Subscription purchase.",
      translatedText:
        '2.1 라이선스 및 사용권. 라이선시가 구독료를 지급하고 본 계약, 해당 제품별 조건, 구독 조건 및 문서의 조건을 준수하는 대가로, ABBYY는 라이선시에게 구독 기간 동안 허가된 용도로만 해당 지역 내에서 ABBYY 소프트웨어를 사용할 수 있는 비독점적이고 양도불가능하며 취소가능한 라이선스(이하 "사용권")를 부여한다. ABBYY 소프트웨어의 특정 기능, 추가기능 및/또는 맞춤형 구성요소의 사용은 라이선시의 구독 범위 및/또는 라이선시와 승인된 공급자 간의 서면계약 및/또는 구독 구매에 수반되는 문서에 의해 제한될 수 있다.',
    },
    {
      segmentId: 35,
      sourceText:
        "2.2 ISV Distribution. This section 2.2 (ISV Distribution) and any of its subsections shall only apply if ISV Distribution is allowed in Appendix A. b)\tISV Distribution. If ISV Distribution of ABBYY Software is allowed, then Usage Rights include a royalty-bearing, revocable, non-assignable. non-exclusive and non-transferable right to distribute copies of ABBYY Software solely integrated into or otherwise in conjunction with Application. Licensee may not distribute copies of ABBYY Software or parts of ABBYY Software separately from Application. ABBYY Software should be distributed within Application and only as a whole. Any other form of distribution of ABBYY Software or its parts is prohibited. For ISV Distribution the following restriction shall apply:",
      translatedText:
        "2.2 ISV 배포. 본 제2.2조(ISV 배포) 및 그 하위조항은 부록 A에서 ISV 배포가 허용되는 경우에만 적용된다. b) ISV 배포. ABBYY 소프트웨어의 ISV 배포가 허용되는 경우, 사용권은 애플리케이션에만 통합되거나 달리 애플리케이션과 함께 ABBYY 소프트웨어의 사본을 배포할 수 있는 로열티 부과, 취소가능, 양도불가능, 비독점적이고 양도불가능한 권리를 포함한다. 라이선시는 애플리케이션과 별도로 ABBYY 소프트웨어 또는 ABBYY 소프트웨어의 일부를 배포할 수 없다. ABBYY 소프트웨어는 애플리케이션 내에서 전체로만 배포되어야 한다. ABBYY 소프트웨어 또는 그 일부를 다른 형태로 배포하는 것은 금지된다. ISV 배포의 경우 다음 제한이 적용된다:",
    },
    {
      segmentId: 36,
      sourceText:
        "2.2.1 No Right to Sublicense. ABBYY does not grant the Licensee the right to sublicense ABBYY Software in any way and by any means on a standalone basis or including without limitation by signing original equipment manufacturer agreements involving the manufacturing and distribution of Application by any third party using any other brand name other than the name of Application stipulated in Appendix A without ABBYY’s prior written consent.",
      translatedText:
        "2.2.1 재라이선스 권리 부존재. ABBYY는 ABBYY의 사전 서면동의 없이 독립적으로 또는 제3자가 부록 A에 명시된 애플리케이션의 명칭 이외의 다른 브랜드명을 사용하여 애플리케이션을 제조 및 유통하는 주문자상표부착생산업체 계약 체결을 포함하되 이에 한정되지 않는 방식으로 ABBYY 소프트웨어를 재라이선스할 권리를 라이선시에게 부여하지 아니한다.",
    },
    {
      segmentId: 37,
      sourceText:
        "2.2.2 Use with Application. Licensee may not integrate ABBYY Software with and distribute it with any software or integrated software and hardware systems other than Application. If Licensee intends to integrate ABBYY Software with and distribute it with a software solution other than Application, Licensee shall enter into an additional agreement with ABBYY to supplement Appendix A with a description of the intended solution.",
      translatedText:
        "2.2.2 애플리케이션과의 사용. 라이선시는 ABBYY 소프트웨어를 애플리케이션 이외의 소프트웨어 또는 통합 소프트웨어 및 하드웨어 시스템과 통합하여 배포할 수 없다. 라이선시가 ABBYY 소프트웨어를 애플리케이션 이외의 소프트웨어 솔루션과 통합하여 배포하고자 하는 경우, 라이선시는 의도된 솔루션에 대한 설명으로 부록 A를 보완하기 위해 ABBYY와 추가계약을 체결하여야 한다.",
    },
    {
      segmentId: 38,
      sourceText:
        "2.3 Use by Third Parties. Licensee may permit Authorized Users to exercise the Usage Rights on Licensee’s behalf, provided that Licensee shall be responsible for: (a) ensuring that such Authorized Users comply with this Agreement; and (b) any breach of this Agreement by such Authorized Users.",
      translatedText:
        "2.3 제3자에 의한 사용. 라이선시는 수권사용자가 라이선시를 대신하여 사용권을 행사하도록 허용할 수 있으며, 다만 라이선시는 (a) 해당 수권사용자가 본 계약을 준수하도록 보장하고, (b) 해당 수권사용자의 본 계약 위반에 대하여 책임을 진다.",
    },
    {
      segmentId: 39,
      sourceText:
        "2.4 Evaluation Versions. If ABBYY grants Licensee Usage Rights for ABBYY Software on a trial, evaluation, beta, pre-release, pre-commercial, not for resale or other free-of-charge basis (“Evaluation Version”), Licensee may only use the Evaluation Version for demonstration, verification, testing and similar purposes and only for a limited period as further defined in the respective Protection Key, Quote or otherwise specified by ABBYY in writing. ABBYY, in its discretion, may terminate Licensee’s Usage Rights to the Evaluation Version at any time, at which point Licensee shall immediately cease using the ABBYY Software. Licensee shall not put Evaluation Version into production use. ABBYY provides Evaluation Version “AS-IS” without support and/or any express or implied warranty or indemnity for any problems or issues, and ABBYY shall not have any liability related to Licensee’s use of the Evaluation Version.",
      translatedText:
        '2.4 평가버전. ABBYY가 ABBYY 소프트웨어에 대한 라이선시 사용권을 시험, 평가, 베타, 출시전, 상업전, 재판매금지 또는 기타 무료 기준으로 부여하는 경우("평가버전"), 라이선시는 각 보호키, 견적서 또는 ABBYY가 서면으로 달리 정의한 제한된 기간 동안 시연, 검증, 테스트 및 유사한 목적으로만 평가버전을 사용할 수 있다. ABBYY는 재량에 따라 언제든지 평가버전에 대한 라이선시의 사용권을 해지할 수 있으며, 이 시점에서 라이선시는 ABBYY 소프트웨어의 사용을 즉시 중단하여야 한다. 라이선시는 평가버전을 실제 운영에 사용하여서는 아니 된다. ABBYY는 문제 또는 이슈에 대한 지원 및 명시적 또는 묵시적 보증이나 면책 없이 평가버전을 "현재 상태"로 제공하며, ABBYY는 라이선시의 평가버전 사용과 관련하여 어떠한 책임도 지지 아니한다.',
    },
    {
      segmentId: 40,
      sourceText:
        "2.5 Support and Maintenance. Except as may be specified otherwise in the applicable Quote, the default level of support and maintenance services is provided to Licensee as a part of Subscription. Support and maintenance services shall be provided to Licensee in accordance with the SM Terms which are incorporated herein by reference. In case of contradiction between the SM Terms and this Agreement, the SM Terms shall prevail. Any updates, upgrades, supplementary software code and any ABBYY Software component provided to Licensee by ABBYY in the course of the support and maintenance services shall be considered a part of ABBYY Software and subject to the terms and conditions of this Agreement. Should Licensee wish to have an extended level of support and maintenance services, Licensee, if eligible, may purchase such an extended level of support and maintenance services from an Approved Source.",
      translatedText:
        "2.5 지원 및 유지보수. 해당 견적서에 달리 명시된 경우를 제외하고, 기본 수준의 지원 및 유지보수 서비스는 구독의 일환으로 라이선시에게 제공된다. 지원 및 유지보수 서비스는 본 계약에 참조로 포함된 SM 조건에 따라 라이선시에게 제공된다. SM 조건과 본 계약 간에 모순이 있을 경우 SM 조건이 우선한다. 지원 및 유지보수 서비스 과정에서 ABBYY가 라이선시에게 제공한 업데이트, 업그레이드, 보충 소프트웨어 코드 및 ABBYY 소프트웨어 구성요소는 ABBYY 소프트웨어의 일부로 간주되며 본 계약의 조건에 따른다. 라이선시가 연장된 수준의 지원 및 유지보수 서비스를 원하는 경우, 적격한 경우 라이선시는 승인된 공급자로부터 해당 연장된 수준의 지원 및 유지보수 서비스를 구매할 수 있다.",
    },
    {
      segmentId: 41,
      sourceText:
        "2.6 Upgrades and Updates. If ABBYY provides Upgrades and Updates (both as defined in SM Terms) as a part of the Subscription, ABBYY will do so in accordance with the SM Terms. ABBYY may develop and provide Updates and Upgrades in its sole discretion, and Licensee agrees that ABBYY has no obligation to develop any Updates at all or for particular issues. Licensee further agrees that all Updates and Upgrades will be deemed ABBYY Software, and related documentation will be deemed Documentation, all subject to all terms and conditions of this Agreement. Licensee acknowledges that ABBYY may provide all Updates via download from a website designated by ABBYY and that Licensee’s receipt thereof will require an internet connection, which connection is Licensee’s sole responsibility. ABBYY has no obligation to provide Updates via any other media. Support and maintenance services do not include any software that ABBYY may issue as a separate or new product, and ABBYY may determine whether any issuance qualifies as a new product, or Update or Upgrade in its sole discretion.",
      translatedText:
        "2.6 업그레이드 및 업데이트. ABBYY가 구독의 일부로서 업그레이드 및 업데이트(SM 조건에 정의됨)를 제공하는 경우, ABBYY는 SM 조건에 따라 이를 수행한다. ABBYY는 단독 재량으로 업데이트 및 업그레이드를 개발 및 제공할 수 있으며, 라이선시는 ABBYY가 업데이트를 전혀 개발하지 아니하거나 특정 쟁점에 대해 개발하지 아니할 의무가 없음에 동의한다. 라이선시는 또한 모든 업데이트 및 업그레이드가 ABBYY 소프트웨어로 간주되며, 관련 문서는 본 계약의 모든 조건에 따라 문서로 간주됨에 동의한다. 라이선시는 ABBYY가 지정한 웹사이트에서 다운로드를 통해 모든 업데이트를 제공할 수 있으며, 이를 수령하기 위해서는 인터넷 연결이 필요함을 인정하며, 이러한 연결은 라이선시의 단독 책임이다. ABBYY는 다른 매체를 통해 업데이트를 제공할 의무가 없다. 지원 및 유지보수 서비스는 ABBYY가 별도 또는 신규 제품으로 발행할 수 있는 소프트웨어를 포함하지 아니하며, ABBYY는 발행이 신규 제품 또는 업데이트 또는 업그레이드에 해당하는지를 단독 재량으로 결정할 수 있다.",
    },
    {
      segmentId: 42,
      sourceText:
        "2.7 Use with Third-Party Products. Any use of ABBYY Software together with third-party products, including but not limited to connectors, is solely at Licensee’s risk. Licensee is responsible for complying with any third-party provider terms, including its privacy policy. ABBYY does not provide support or guarantee ongoing integration support for products that are not a native part of ABBYY Software.",
      translatedText:
        "2.7 제3자 제품과의 사용. ABBYY 소프트웨어를 커넥터를 포함하되 이에 한정되지 않는 제3자 제품과 함께 사용하는 것은 전적으로 라이선시의 위험부담이다. 라이선시는 개인정보보호정책을 포함한 제3자 제공자의 약관을 준수할 책임이 있다. ABBYY는 ABBYY 소프트웨어의 고유 구성요소가 아닌 제품에 대하여 지원을 제공하거나 지속적인 통합 지원을 보장하지 아니한다.",
    },
    {
      segmentId: 43,
      sourceText: "3. Usage Requirements and Restrictions",
      translatedText: "3. 사용요건 및 제한",
    },
    {
      segmentId: 44,
      sourceText:
        "3.1 General Restrictions. Unless expressly agreed by ABBYY in writing, Licensee may not:",
      translatedText:
        "3.1 일반적 제한. ABBYY가 서면으로 명시적으로 합의하지 아니하는 한 라이선시는 다음 각 호의 행위를 하여서는 아니 된다:",
    },
    {
      segmentId: 45,
      sourceText:
        "(a) transfer, rent, lease, sell, sublicense, make the functionality of any ABBYY Software available to any third party, authorize all or any portion of ABBYY Software to be copied;",
      translatedText:
        "(a) ABBYY 소프트웨어의 기능을 제3자에게 이전, 대여, 임대, 판매, 재라이선스하거나 이용가능하게 하는 행위 또는 ABBYY 소프트웨어의 전부 또는 일부의 복사를 허가하는 행위",
    },
    {
      segmentId: 46,
      sourceText:
        "(b) remove, modify, or conceal any ABBYY Software identification, copyright, proprietary, intellectual property notices or other marks;",
      translatedText:
        "(나) ABBYY 소프트웨어의 식별, 저작권, 독점권, 지적재산권 통지 또는 기타 표시를 제거, 수정 또는 은폐하는 행위",
    },
    {
      segmentId: 47,
      sourceText:
        "(c) modify, change, correct errors, translate or make derivative works of ABBYY Software, or adapt (including any changes to enable ABBYY Software to run on Licensee’s hardware), or make any changes to the object code of ABBYY Software, applications and databases contained in ABBYY Software other than those provided for by ABBYY Software and described in the Documentation;",
      translatedText:
        "(다) ABBYY 소프트웨어를 수정, 변경, 오류 수정, 번역하거나 2차 저작물을 작성하거나, 적응(ABBYY 소프트웨어가 라이선시의 하드웨어에서 실행될 수 있도록 하는 변경을 포함)하거나, ABBYY 소프트웨어에 포함된 ABBYY 소프트웨어의 객체 코드, 애플리케이션 및 데이터베이스에 대해 ABBYY 소프트웨어가 제공하고 문서에 기재된 것 이외의 변경을 하는 행위",
    },
    {
      segmentId: 48,
      sourceText:
        "(d) reverse engineer, decompile (i.e. reproduce and transform the object code into source code), decrypt, disassemble, or otherwise attempt to derive the source code of ABBYY Software or any of its parts, except, and only to the extent, that such activity is expressly permitted by applicable law notwithstanding this limitation, in which case any information so discovered must not be disclosed to third parties except if disclosure is required by law and such information must be promptly disclosed to ABBYY and all such information shall be deemed to be confidential and proprietary information of ABBYY;",
      translatedText:
        "(라) ABBYY 소프트웨어 또는 그 일부의 소스 코드를 역설계, 디컴파일(즉, 객체 코드를 소스 코드로 복제 및 변환), 복호화, 분해하거나 기타 방법으로 도출하려고 시도하는 행위. 다만, 이러한 활동이 본 제한에도 불구하고 관련 법률에 의해 명시적으로 허용되는 경우에 한하며, 이 경우 발견된 정보는 법률에 의해 공개가 요구되는 경우를 제외하고는 제3자에게 공개되어서는 아니 되며, 해당 정보는 ABBYY에 즉시 공개되어야 하고, 모든 정보는 ABBYY의 비밀 및 독점 정보로 간주된다.",
    },
    {
      segmentId: 49,
      sourceText:
        "(e) provide access to ABBYY Software to anyone except for Authorized Users, including without limitation in a multi-user system, virtual environment, or via the Internet;",
      translatedText:
        "(마) 다중 사용자 시스템, 가상 환경 또는 인터넷을 통한 경우를 포함하여 수권 사용자를 제외한 누구에게도 ABBYY 소프트웨어에 대한 접근을 제공하는 행위",
    },
    {
      segmentId: 50,
      sourceText: "(f) use ABBYY Content other than permitted;",
      translatedText: "(f) 허용된 범위를 초과하여 ABBYY 콘텐츠를 사용하는 행위",
    },
    {
      segmentId: 51,
      sourceText: "(g) use ABBYY Software for benchmarking purposes; or",
      translatedText:
        "(g) 벤치마킹 목적으로 ABBYY 소프트웨어를 사용하는 행위 또는",
    },
    {
      segmentId: 52,
      sourceText:
        "(h) unless otherwise provided in the applicable Product Specific Terms, if ABBYY Software is deployed or used in a virtual or cloud software environment, including but not limited to VMware, Citrix, Microsoft Azure, Amazon AWS, or ghosting, at no time shall actual usage of ABBYY Software exceed the scope of the license purchased (obtained) by Licensee from the Approved Source (including, but not limited to, the restriction on the number of Volume Units processed) and Licensee shall not restore a saved state of a virtual environment to circumvent the limitation on the number of Volume Units processed, or circumvent Subscription Term limitation by setting an untrue date in a virtual environment. .",
      translatedText:
        "(h) 해당 제품 특정 조건에 달리 규정되지 않는 한, ABBYY 소프트웨어가 VMware, Citrix, Microsoft Azure, Amazon AWS 또는 고스팅을 포함하되 이에 한정되지 않는 가상 또는 클라우드 소프트웨어 환경에서 배포되거나 사용되는 경우, ABBYY 소프트웨어의 실제 사용은 언제라도 라이선시가 승인된 출처로부터 구매(취득)한 라이선스의 범위(처리된 부피 단위 수에 대한 제한을 포함하되 이에 한정하지 않음)를 초과하여서는 아니 되며, 라이선시는 처리된 부피 단위 수에 관한 제한을 회피하기 위해 가상 환경의 저장 상태를 복원하거나 가상 환경에서 거짓 날짜를 설정하여 구독 기간 제한을 회피하여서는 아니 된다",
    },
    {
      segmentId: 53,
      sourceText:
        "3.2 Sunsetting. ABBYY may end the life of ABBYY Software, including any component functionality (“EOL”), by posting a written notice on https://www.abbyy.com/support/eol/. If Licensee have prepaid a fee for a Subscription to ABBYY Software that becomes EOL before the expiration of Licensee’s then-current Subscription Term, ABBYY will either (i) grant to Licensee the Usage Rights during the pre-paid Subscription Term without guaranteeing to Licensee availability of any support and maintenance services for such ABBYY Software after it becomes EOL, or (ii) use commercially reasonable efforts to transition Licensee to a substantially similar ABBYY software, or (iii) pro-rata credit Licensee any unused portion of the prepaid fee for Subscription to ABBYY Software that has been declared EOL (“EOL Credit”). The EOL Credit will be calculated from the last date the respective ABBYY Software is available to the last date of the applicable pre-paid Subscription Term. The EOL Credit may only be applied towards future purchases of ABBYY products.",
      translatedText:
        '3.2 일몰. ABBYY는 https://www.abbyy.com/support/eol/에 서면 통지를 게재함으로써 구성 요소 기능을 포함한 ABBYY 소프트웨어의 수명(이하 "EOL")을 종료할 수 있다. 라이선시가 당시 구독 기간 만료 전에 EOL가 되는 ABBYY 소프트웨어 구독에 대한 수수료를 선납한 경우, ABBYY는 (i) 해당 ABBYY 소프트웨어가 EOL가 된 후 지원 및 유지보수 서비스의 이용 가능성을 보장하지 않고 선납 구독 기간 동안 사용권을 라이선시에게 부여하거나, (ii) 라이선시를 실질적으로 유사한 ABBYY 소프트웨어로 전환하기 위해 상업적으로 합리적인 노력을 다하거나, (iii) EOL로 선언된 ABBYY 소프트웨어 구독에 대한 선납 수수료의 미사용 부분을 라이선시에게 비례적으로 크레딧(이하 "EOL 크레딧")한다. EOL 크레딧은 해당 ABBYY 소프트웨어가 이용 가능한 마지막 날부터 해당 선납 구독 기간의 마지막 날까지 계산된다. EOL 크레딧은 ABBYY 제품의 향후 구매에만 적용될 수 있다',
    },
    {
      segmentId: 54,
      sourceText:
        "3.3 Third Party Software, Open Source Software. ABBYY Software may contain components of third-party software, which are the property of their respective owners. Such components are not owned by ABBYY and are subject to separate license terms as set out in the Documentation and/or available online at https://www.abbyy.com/eula/tpt/. ABBYY makes no warranties for any Third Party Software or Open Source Software.",
      translatedText:
        "3.3 제3자 소프트웨어, 오픈 소스 소프트웨어. ABBYY 소프트웨어는 각 소유자의 재산인 제3자 소프트웨어의 구성 요소를 포함할 수 있다. 이러한 구성 요소는 ABBYY가 소유하지 않으며, 문서 및/또는 https://www.abbyy.com/eula/tpt/에서 온라인으로 이용 가능한 별도의 라이선스 조건의 적용을 받는다. ABBYY는 제3자 소프트웨어 또는 오픈 소스 소프트웨어에 대해 어떠한 보증도 하지 않는다.",
    },
    {
      segmentId: 55,
      sourceText:
        "3.4 Code Samples. Licensee may use source code of sample applications that use ABBYY Software, as may be made available by ABBYY (“Code Samples”), in Licensee’s software solution, but all Code Samples are the sole and exclusive property of ABBYY. Ownership of modifications to Code Samples shall remain with the party making such modifications; however, if Licensee discloses to ABBYY modifications made to Code Samples by sending them to ABBYY support and maintenance service or by any other means, Licensee hereby grants an irrevocable, non-exclusive, perpetual, transferable, worldwide, royalty-free license to make, use, sell, offer to sell, import, export, license, sub-license Licensee’s modifications to Code Samples, without any remuneration due to Licensee. Licensee represents and warrants that the aforementioned rights granted to ABBYY shall be free of any third-party rights or encumbrances and that Licensee have obtained all necessary rights to grant such rights to ABBYY and that Licensee had, if applicable, paid all necessary remuneration to authors of such modifications. If Licensee does not wish to grant to ABBYY such license to the modifications to Code Samples, Licensee shall not disclose to ABBYY the source code of such modifications.",
      translatedText:
        '3.4 코드 샘플. 라이선시는 ABBYY가 제공하는 ABBYY 소프트웨어를 사용하는 샘플 애플리케이션의 소스 코드(이하 "코드 샘플")를 라이선시의 소프트웨어 솔루션에 사용할 수 있으나, 모든 코드 샘플은 ABBYY의 단독 및 배타적 재산이다. 코드 샘플의 수정에 대한 소유권은 해당 수정을 하는 당사자에게 유지된다. 다만, 라이선시가 ABBYY 지원 및 유지보수 서비스에 코드 샘플의 수정을 전송하거나 기타 방법으로 ABBYY에 공개하는 경우, 라이선시는 라이선시에게 지급해야 할 보수 없이 코드 샘플에 대한 라이선시의 수정을 제작, 사용, 판매, 판매 제안, 수입, 수출, 라이선스 및 재라이선스할 수 있는 취소 불가능하고 비독점적이며 영구적이고 양도 가능하며 전 세계적이고 로열티 없는 라이선스를 부여한다. 라이선시는 ABBYY에 부여된 상기 권리가 제3자의 권리 또는 부담으로부터 자유롭고, 라이선시가 ABBYY에 해당 권리를 부여하기 위해 필요한 모든 권리를 취득하였으며, 해당되는 경우 해당 수정의 저자에게 필요한 모든 보수를 지급하였음을 진술하고 보장한다. 라이선시가 ABBYY에게 코드 샘플의 수정 라이선스를 부여하기를 원하지 않는 경우, 라이선시는 해당 수정의 소스 코드를 ABBYY에 공개하지 않는다.',
    },
    {
      segmentId: 56,
      sourceText:
        "3.5 Ownership. Licensee acknowledges and agrees that ABBYY Software and Documentation are provided under license, and not sold, to Licensee. Licensee does not acquire any ownership interest in ABBYY Software, ABBYY Content or Documentation under this Agreement, or any other rights thereto, other than to use the same in accordance with the license granted and subject to all terms, conditions, and restrictions under this Agreement. ABBYY and its licensors and service providers reserve and shall retain their entire right, title, and interest in and to ABBYY Software and all Intellectual Property Rights arising out of or relating to ABBYY Software, except as expressly granted to Licensee in this Agreement. Licensee shall use commercially reasonable efforts to safeguard all ABBYY Software (including all copies thereof and Protection Keys) from infringement, misappropriation, theft, misuse, or unauthorized access.",
      translatedText:
        "3.5 소유권. 라이선시는 ABBYY 소프트웨어 및 문서가 라이선시에게 판매되지 않고 라이선스에 따라 제공됨을 인정하고 이에 동의한다. 라이선시는 본 계약에 따라 부여된 라이선스에 따라 사용하는 것을 제외하고는 ABBYY 소프트웨어, ABBYY 콘텐츠 또는 문서에 대한 소유권 또는 기타 권리를 취득하지 않는다. ABBYY 및 그 라이선서와 서비스 제공자는 본 계약에서 라이선시에게 명시적으로 부여된 경우를 제외하고, ABBYY 소프트웨어 및 ABBYY 소프트웨어와 관련하여 발생하는 모든 지식재산권에 대한 모든 권리, 소유권 및 이익을 유보하고 보유한다. 라이선시는 모든 ABBYY 소프트웨어(모든 사본 및 보호 키 포함)를 침해, 유용, 도난, 오용 또는 무단 접근으로부터 보호하기 위해 상업적으로 합리적인 노력을 다하여야 한다.",
    },
    {
      segmentId: 57,
      sourceText:
        "3.6 Requirements for ISV Distribution. With respect to ISV Distribution of ABBYY Software, in order to provide End Users rights to use ABBYY Software within Application, Licensee shall:",
      translatedText:
        "3.6 ISV 유통 요건. ABBYY 소프트웨어의 ISV 유통과 관련하여, 최종 사용자에게 애플리케이션 내에서 ABBYY 소프트웨어를 사용할 권리를 제공하기 위해, 라이선시는 다음을 이행하여야 한다:",
    },
    {
      segmentId: 58,
      sourceText:
        "3.6.1 only distribute Application and ABBYY Software in accordance with section 2.2 of this Agreement;",
      translatedText:
        "3.6.1 본 계약 제2.2조에 따라서만 애플리케이션 및 ABBYY 소프트웨어를 배포한다",
    },
    {
      segmentId: 59,
      sourceText:
        "3.6.2 transfer ABBYY Software licenses (except for development license(s)) to End Users directly or via resellers as a simple non-exclusive, non-transferable, personal license within the Territory during the term of this Agreement solely for the purpose of using the ABBYY Software integrated with Application for End User’s internal purposes during the term of the respective Subscription;",
      translatedText:
        "3.6.2 본 계약 기간 동안 각 구독 기간 동안 최종사용자의 내부 목적을 위해 애플리케이션과 통합된 ABBYY 소프트웨어를 사용할 목적으로만, 본 계약 지역 내에서 단순 비독점적이고 양도불가능한 개인 라이선스로서 ABBYY 소프트웨어 라이선스(개발 라이선스 제외)를 최종사용자에게 직접 또는 재판매자를 통해 양도한다",
    },
    {
      segmentId: 60,
      sourceText:
        "3.6.3 oblige End Users not to subsequently transfer ABBYY Software licenses and/or ABBYY Software from an End User to any third party;",
      translatedText:
        "3.6.3 최종 사용자가 ABBYY 소프트웨어 라이선스 및/또는 ABBYY 소프트웨어를 제3자에게 추후 양도하지 않도록 의무를 부과한다",
    },
    {
      segmentId: 61,
      sourceText:
        "3.6.4 inform End Users that the licenses are on a Subscription basis and will be discontinued once the Subscription terminates;",
      translatedText:
        "3.6.4 최종 사용자에게 라이선스가 구독 기반이며 구독이 종료되면 중단될 것임을 통지한다",
    },
    {
      segmentId: 62,
      sourceText:
        '3.6.5 display the "Powered by ABBYY" logo pursuant to the ABBYY Logo Usage Guidelines posted at http://www.abbyy.com/legal/abbyy_logo_usage_guide on the Application website, in the Application UI, and in the Application documentation; and',
      translatedText:
        '3.6.5 애플리케이션 웹사이트, 애플리케이션 UI 및 애플리케이션 문서에 http://www.abbyy.com/legal/abbyy_logo_usage_guide에 게재된 ABBYY 로고 사용 지침에 따라 "Powered by ABBYY" 로고를 표시한다',
    },
    {
      segmentId: 63,
      sourceText:
        "3.6.6 include the following or equivalent minimum terms in the Application End User license agreement:",
      translatedText:
        "3.6.6 애플리케이션 최종 사용자 라이선스 계약에 다음 또는 이에 상응하는 최소 조건을 포함한다:",
    },
    {
      segmentId: 64,
      sourceText:
        "(i)\tApplication utilizes ABBYY Software, referred by its exact name;",
      translatedText:
        "(i) 애플리케이션은 정확한 명칭으로 지칭되는 ABBYY 소프트웨어를 활용한다.",
    },
    {
      segmentId: 65,
      sourceText:
        "(ii)\tABBYY shall not be liable to an End User for any general, special, direct, indirect, consequential, incidental, or lost profits or other damages arising out of or related to ABBYY Software;",
      translatedText:
        "(ii) ABBYY는 ABBYY 소프트웨어로부터 또는 이와 관련하여 발생하는 일반적, 특별, 직접적, 간접적, 결과적, 부수적 손해 또는 일실이익이나 기타 손해에 대해 최종 사용자에게 책임을 지지 아니한다.",
    },
    {
      segmentId: 66,
      sourceText:
        "(iii)\tEnd User shall not: (a) decompile or reverse engineer ABBYY Software; (b) modify, publish, transmit, license, sublicense, transfer, sell, distribute, reproduce, create derivative or collective works from ABBYY Software; (c) disclose results of any services or program benchmark tests of ABBYY Software without ABBYY’s prior written consent; (d) license, sell, rent, lease, transfer, assign, distribute, display, host, rurce, disclose, permit timesharing or service bureau use, or otherwise commercially exploit or make ABBYY Software available to any third party other than as expressly permitted under this Agreement; (e) use ABBYY Software to transmit files, content, photographs, videos, personal or technical data or any other type of information or data (collectively, “User Content”) that is defamatory or libelous or for which transmission would constitute infringement of third-party intellectual property rights; (f) use ABBYY Software to transmit any viruses, malicious code, trojans, worms, corrupted files, or any other similar software that may damage the operation of another's computer, data or property, or transmit any other harmful code technology; or (g) export or re-export ABBYY Software in violation of any export provisions in force in the USA and in the country where the End User purchased the Application, or in violation of any other applicable legislation.",
      translatedText:
        '(iii) 최종 사용자는 다음 행위를 하여서는 아니 된다: (a) ABBYY 소프트웨어를 디컴파일하거나 역설계하는 행위; (b) ABBYY 소프트웨어를 수정, 공표, 전송, 라이선스 허여, 서브라이선스 허여, 이전, 판매, 배포, 복제하거나 이로부터 파생물 또는 집합 저작물을 작성하는 행위; (c) ABBYY의 사전 서면 동의 없이 ABBYY 소프트웨어의 서비스 또는 프로그램 벤치마크 테스트 결과를 공개하는 행위; (d) 본 계약에서 명시적으로 허용된 경우를 제외하고, ABBYY 소프트웨어를 제3자에게 라이선스 허여, 판매, 대여, 임대, 이전, 양도, 배포, 전시, 호스팅, 공개하거나 타임셰어링 또는 서비스 사무소 사용을 허용하거나 기타 상업적으로 이용하거나 이용 가능하게 하는 행위; (e) 명예훼손적이거나 비방적인 파일, 콘텐츠, 사진, 비디오, 개인 또는 기술 데이터 또는 기타 유형의 정보나 데이터(총칭하여 "사용자 콘텐츠")를 전송하거나 그 전송이 제3자의 지적재산권 침해를 구성하는 경우 ABBYY 소프트웨어를 사용하는 행위; (f) 타인의 컴퓨터, 데이터 또는 재산의 운영에 손상을 초래할 수 있는 바이러스, 악성 코드, 트로이 목마, 웜, 손상된 파일 또는 기타 유사한 소프트웨어나 기타 유해한 코드 기술을 전송하기 위해 ABBYY 소프트웨어를 사용하는 행위; 또는 (g) 미국 및 최종 사용자가 애플리케이션을 구매한 국가에서 시행 중인 수출 조항 또는 기타 관련 법령을 위반하여 ABBYY 소프트웨어를 수출하거나 재수출하는 행위.',
    },
    {
      segmentId: 67,
      sourceText:
        "(iv)\tall disputes between Licensee and End Users, whether or not relating to any information, data or forms available on or through ABBYY Software, will be settled by and between Licensee and End User; ABBYY shall bear no responsibility for resolving such disputes and shall have no obligation to be involved in any way in any such dispute;",
      translatedText:
        "(iv) ABBYY 소프트웨어에서 또는 ABBYY 소프트웨어를 통해 이용 가능한 정보, 데이터 또는 양식과의 관련 여부와 관계없이, 라이선시와 최종 사용자 간의 모든 분쟁은 라이선시와 최종 사용자 간에 해결된다. ABBYY는 이러한 분쟁 해결에 대한 책임을 지지 아니하며, 이러한 분쟁에 어떠한 방식으로도 관여할 의무를 지지 아니한다.",
    },
    {
      segmentId: 68,
      sourceText:
        "(v)\twhen using ABBYY Software End User shall comply with all applicable legal requirements regarding privacy and data protection;",
      translatedText:
        "(v) ABBYY 소프트웨어를 사용할 때 최종 사용자는 개인정보 보호 및 데이터 보호에 관한 모든 적용 가능한 법적 요건을 준수하여야 한다.",
    },
    {
      segmentId: 69,
      sourceText:
        "(vi) ABBYY Software is provided “as is.” ABBYY does not warrant that ABBYY Software is error free, nor will it be liable for any damages, including damages for loss of business profits or disclosure of confidential information; and",
      translatedText:
        '(vi) ABBYY 소프트웨어는 "있는 그대로" 제공된다. ABBYY는 ABBYY 소프트웨어가 오류가 없음을 보증하지 않으며, 사업 이익 손실 또는 비밀 정보 공개에 대한 손해를 포함하여 어떠한 손해에 대해서도 책임을 지지 않는다.',
    },
    {
      segmentId: 70,
      sourceText:
        "(vii) If any part of the Application End User license agreement is found void and unenforceable, it will not affect the validity of the balance of the Application End User license agreement, which shall remain valid and enforceable according to its terms. The End User license agreement shall not prejudice the statutory rights of any party dealing as a consumer.",
      translatedText:
        "(vii) 애플리케이션 최종 사용자 라이선스 계약의 일부가 무효이고 집행 불가능한 것으로 판명되는 경우, 이는 애플리케이션 최종 사용자 라이선스 계약의 잔여 부분의 유효성에 영향을 미치지 않으며, 이는 그 조건에 따라 여전히 유효하고 집행 가능하다. 최종 사용자 라이선스 계약은 소비자로서 거래하는 당사자의 법정 권리를 침해하지 않는다.",
    },
    {
      segmentId: 71,
      sourceText: "4. Fees; Payment",
      translatedText: "4. 수수료 및 지급",
    },
    {
      segmentId: 72,
      sourceText:
        "4.1 Licensee shall pay ABBYY the fees indicated in Quote in accordance with these payment terms and in currency specified in Quote.",
      translatedText:
        "4.1 라이선시는 본 지급 조건에 따라 견적서에 명시된 수수료를 견적서에 명시된 통화로 ABBYY에 지급하여야 한다.",
    },
    {
      segmentId: 73,
      sourceText:
        "4.2 If Licensee fails to pay ABBYY’s invoice by the payment due date, ABBYY may cancel the respective Quote and/or Usage Rights.",
      translatedText:
        "4.2 라이선시가 지급 기일까지 ABBYY의 청구서를 지급하지 않는 경우, ABBYY는 해당 견적서 및/또는 사용권을 취소할 수 있다.",
    },
    {
      segmentId: 74,
      sourceText:
        "4.3 No Refund. Except as and to the extent a refund is expressly provided for elsewhere in this Agreement, in no event shall ABBYY be obliged to return to Licensee any payments and fees for ABBYY Software, or any portion thereof or interest thereon.",
      translatedText:
        "4.3 환불 금지. 본 계약의 다른 조항에서 명시적으로 환불이 규정된 경우를 제외하고, 어떠한 경우에도 ABBYY는 라이선시에게 ABBYY 소프트웨어에 대한 지급금 및 수수료, 그 일부 또는 이에 대한 이자를 반환할 의무가 없다.",
    },
    {
      segmentId: 75,
      sourceText:
        "4.4 Late payment interest. If Licensee’s account falls into arrears, delinquent invoices are subject to interest of one and one-half percent (1.5%) per month on any outstanding balance, or the maximum permitted by law, whichever is less, plus all expenses of collection.",
      translatedText:
        "4.4 지연 지급 이자. 라이선시의 계좌가 연체된 경우, 연체된 청구서에는 미상환 잔액에 대해 월 1.5%의 이자 또는 법률상 허용되는 최대 이자 중 적은 금액과 모든 추심 비용이 부과된다.",
    },
    {
      segmentId: 76,
      sourceText:
        "4.5 Payments. Licensee shall submit payment to ABBYY by wire transfer or a method approved by ABBYY in writing. Wire information shall be as per the applicable invoice or as ABBYY specifies by written notice. All bank transfer charges will be at the Licensee's expense.",
      translatedText:
        "4.5 지급. 라이선시는 전신환 또는 ABBYY가 서면으로 승인한 방법으로 ABBYY에 지급하여야 한다. 전신환 정보는 해당 청구서에 따르거나 ABBYY가 서면 통지로 명시한 바에 따른다. 모든 은행 송금 수수료는 라이선시가 부담한다.",
    },
    {
      segmentId: 77,
      sourceText:
        "4.6 No Set-off. Licensee may not set off payments due to ABBYY against any amounts ABBYY may owe to Licensee.",
      translatedText:
        "4.6 상계 금지. 라이선시는 ABBYY에 대한 지급 의무를 ABBYY가 라이선시에게 부담하는 금액과 상계할 수 없다.",
    },
    {
      segmentId: 78,
      sourceText:
        "4.7 Price changes. Before any Subscription renewal ABBYY may increase the Subscription fees for up to 3% from the then current Subscription fees rates unless otherwise mentioned in the relevant Quote.",
      translatedText:
        "4.7 가격 변경. 구독 갱신 전에 ABBYY는 관련 견적서에 달리 명시되지 않는 한 당시 구독 수수료율에서 최대 3%까지 구독 수수료를 인상할 수 있다.",
    },
    {
      segmentId: 79,
      sourceText:
        "4.8 Additional Information. ABBYY in its sole discretion acting reasonably may request from Licensee additional information, which may be reasonably required for compliance and other purposes, before processing Subscription renewal(s); and if such requested additional information is not provided by Licensee within the timeframe defined by ABBYY, then ABBYY may, in its sole discretion, reject or postpone the auto-renewal of Licensee’s existing Subscription, or terminate a then-current Subscription. A portion of Subscription renewal term lapsed due to Licensee’s failure to provide documents or information reasonably required by ABBYY for processing a Subscription renewal, as required by this section, may be reinstated upon Licensee’s payment of a renewal reinstatement fee of up to two percent (2%) of yearly Subscription fee per lapsed month.",
      translatedText:
        "4.8 추가 정보. ABBYY는 합리적으로 행동하는 단독 재량으로 구독 갱신 처리 전에 라이선시에게 준수 및 기타 목적을 위해 합리적으로 요구될 수 있는 추가 정보를 요청할 수 있으며, 라이선시가 ABBYY가 정한 기간 내에 요청된 추가 정보를 제공하지 않는 경우, ABBYY는 단독 재량으로 라이선시의 기존 구독 자동 갱신을 거부하거나 연기하거나 당시 구독을 해지할 수 있다. 본 조에 따라 라이선시가 구독 갱신 처리를 위해 ABBYY가 합리적으로 요구하는 서류 또는 정보를 제공하지 않아 경과한 구독 갱신 기간의 일부는 라이선시가 경과한 월당 연간 구독 수수료의 2% 이내에서 갱신 복원 수수료를 지급함으로써 복원될 수 있다.",
    },
    {
      segmentId: 80,
      sourceText:
        "4.9 Quote Execution. If Licensee executes and/or returns a Quote to ABBYY after the Subscription start date indicated in such Quote, ABBYY may, at its sole discretion, adjust the Subscription start date and the end date, without increasing the total price under such Quote, based on the date ABBYY provides access to the Software to Licensee after Licensee executes and/or returns such Quote to ABBYY and provided that the Subscription Term length under such Quote does not change. ABBYY may reject any Quote at its sole and absolute discretion with or without a cause and with no indemnification obligation to Licensee.",
      translatedText:
        "4.9 견적서 체결. 라이선시가 해당 견적서에 명시된 구독 개시일 이후에 ABBYY에 견적서를 체결 및/또는 반환하는 경우, ABBYY는 라이선시가 해당 견적서를 체결 및/또는 ABBYY에 반환한 후 ABBYY가 라이선시에게 소프트웨어에 대한 접근을 제공한 날을 기준으로 하여, 해당 견적서에 따른 총 가격을 인상하지 않고 단독 재량으로 구독 개시일 및 종료일을 조정할 수 있다. 단, 해당 견적서에 따른 구독 기간은 변경되지 않아야 한다. ABBYY는 사유 여부와 관계없이 라이선시에 대한 면책 의무 없이 단독의 절대적 재량으로 견적서를 거절할 수 있다.",
    },
    {
      segmentId: 81,
      sourceText:
        "4.10 Termination of Subscription followed by repurchase of the equivalent Subscription within four (4) months of the termination shall be considered a Subscription renewal and shall be subject to administrative reinstatement fee of up to two percent (2%) of yearly Subscription Fee per lapsed month.5. Confidential Information and Data Use",
      translatedText:
        "4.10 구독 해지 후 해지일로부터 4개월 이내에 동등한 구독을 재매수하는 경우, 이는 구독 갱신으로 간주되며, 경과한 월당 연간 구독 수수료의 최대 2%에 해당하는 관리 복원 수수료가 부과된다. 5. 비밀정보 및 데이터 사용",
    },
    {
      segmentId: 82,
      sourceText:
        "5.1 Confidentiality. Receiving Party will hold in confidence and use no less than reasonable degree of care to avoid disclosure of any Confidential Information of disclosing Party to any third party, except for its own and its Affiliates’ and contractors’ employees who have a need to know (“Permitted Recipients”) for five (5) years from Receiving Party obtaining the respective Confidential Information, except with respect to any ABBYY Software (including any code or technical documentation related to such ABBYY Software), for which the obligations of confidentiality shall survive in perpetuity or the longest period of time allowable by applicable law, whichever is shorter. Receiving Party: (a) must ensure that its Permitted Recipients are subject to written confidentiality obligations no less restrictive than the Receiving Party’s obligations under this Agreement, and (b) is liable for any breach of obligations set herein by its Permitted Recipients. Receiving Party may disclose Disclosing Party’s Confidential Information if required under a mandatory regulation, law or court order provided that Receiving Party provides prior notice to Disclosing Party (to the extent legally permissible) and reasonably cooperates, at Disclosing Party’s expense, regarding protective actions pursued by Disclosing Party. Upon the reasonable request of Disclosing Party, Receiving Party will either return, delete or destroy all Confidential Information of Disclosing Party and certify the same.",
      translatedText:
        '5.1 비밀유지. 수령 당사자는 각 비밀정보를 취득한 날로부터 5년 동안 알 필요가 있는 자신 및 자신의 계열사 및 계약자의 직원(이하 "허용된 수령인")을 제외하고, 공개 당사자의 비밀정보를 비밀로 유지하고 제3자에게 공개되지 않도록 합리적인 주의를 기울여야 한다. 단, ABBYY 소프트웨어(해당 ABBYY 소프트웨어와 관련된 코드 또는 기술 문서 포함)에 대해서는 비밀유지 의무가 영구적으로 존속하거나 관련 법률이 허용하는 최장 기간 중 짧은 기간 동안 존속한다. 수령 당사자는: (a) 자신의 허용된 수령인이 본 계약에 따른 수령 당사자의 의무보다 덜 제한적이지 않은 서면 비밀유지 의무를 부담하도록 보장하여야 하며, (b) 자신의 허용된 수령인이 본 계약에 명시된 의무를 위반할 경우 이에 대해 책임을 진다. 수령 당사자는 강행 규정, 법률 또는 법원 명령에 따라 요구되는 경우 공개 당사자의 비밀정보를 공개할 수 있으며, 단, 수령 당사자는 공개 당사자에게(법적으로 허용되는 범위 내에서) 사전 통지를 제공하고, 공개 당사자가 추구하는 보호 조치에 대해 공개 당사자의 비용으로 합리적으로 협력하여야 한다. 공개 당사자의 합리적인 요청이 있을 경우, 수령 당사자는 공개 당사자의 모든 비밀정보를 반환, 삭제 또는 파기하고 이를 인증하여야 한다.',
    },
    {
      segmentId: 83,
      sourceText:
        "5.2 Residual Information. It is important for ABBYY to be able to use its skills, experience, expertise, concepts, ideas, and knowhow learned or acquired while providing ABBYY Software to Licensee. Therefore, Licensee hereby agrees that ABBYY is entitled to retain and use without restriction any generalized knowledge, techniques, methodologies, practices, processes, skills, experience, expertise, concepts, ideas, and knowhow learned or acquired by ABBYY’s personnel in the course of providing ABBYY Software to Licensee solely to the extent that such information is retained in intangible form in the unaided memory of the personnel of ABBYY without intentionally memorizing such information or using any Licensee’s Confidential Information to refresh its recollection. Nothing in this Agreement shall be construed as to prevent ABBYY from such retention and use, and further, such retention and use of any such information shall not be construed as a breach of this Agreement.",
      translatedText:
        "5.2 잔여 정보. ABBYY가 라이선시에게 ABBYY 소프트웨어를 제공하는 과정에서 배우거나 취득한 기술, 경험, 전문성, 개념, 아이디어 및 노하우를 사용할 수 있는 것은 중요하다. 따라서 라이선시는 ABBYY가 라이선시에 ABBYY 소프트웨어를 제공하는 과정에서 ABBYY 직원이 배우거나 취득한 일반화된 지식, 기술, 방법론, 관행, 프로세스, 기능, 경험, 전문성, 개념, 아이디어 및 노하우를 제한 없이 보유 및 사용할 권리가 있음을 동의하며, 이는 해당 정보가 의도적으로 암기되지 않거나 라이선시의 비밀정보를 사용하여 기억을 새롭게 하지 않고 ABBYY 직원의 무형 형태의 무보조 기억에 보유되는 범위 내에서만 해당한다. 본 계약의 어떠한 내용도 ABBYY의 이러한 보유 및 사용을 금지하는 것으로 해석되지 않으며, 이러한 정보의 보유 및 사용은 본 계약의 위반으로 해석되지 않는다.",
    },
    {
      segmentId: 84,
      sourceText: "5.3 Data Processing.",
      translatedText: "5.3 데이터 처리.",
    },
    {
      segmentId: 85,
      sourceText:
        "5.3.1. ABBYY will not process, and will not have an access to, Licensee’s or its representatives’ Personal Data except that, in the course of activation, installation, operation, registration and/or support and maintenance of the ABBYY Software, Licensee may be asked to provide certain Personal Data (such as but not limited to name, email address of a contact person) and technical information (such as but not limited to characteristics of Licensee’s hardware, third party software, serial number of ABBYY Software). Licensee may choose not to provide ABBYY with such Personal Data and/or technical information, in which case Licensee may not be able to obtain support and maintenance services for ABBYY Software available to ABBYY customers who provide their Personal Data and/or technical information, if such Personal Data and/or technical information is essential to provide Licensee with support or maintenance for ABBYY Software or is a requirement of such support and maintenance for ABBYY Software and it does not contradict the applicable law. Licensee agrees not to provide more Personal Data and/or technical information than ABBYY or an Approved Source requires, and Licensee agrees that Personal Data and/or technical information provided may be processed (including but not limited to collected and/or otherwise used) by ABBYY and/or its Affiliates or partners in compliance with applicable law provided that confidentiality of such data and data security is maintained if it is required by applicable law. For more details on how ABBYY processes Personal Data described in this Section, please refer to ABBYY Privacy Policy available at https://www.abbyy.com/privacy/ (“Privacy Policy”). If Licensee uses ABBYY Software in a location with local laws requiring a designated entity to be responsible for collection of Personal Data of individual end users and transfer of data outside of that jurisdiction, Licensee acknowledges that Licensee is the entity responsible for complying with such laws.",
      translatedText:
        '5.3.1. ABBYY는 ABBYY 소프트웨어의 활성화, 설치, 운영, 등록 및/또는 지원 및 유지보수 과정에서 라이선시에게 특정 개인정보(이름, 연락처 이메일 주소 등) 및 기술 정보(라이선시의 하드웨어, 제3자 소프트웨어, ABBYY 소프트웨어 일련번호의 특성 등)를 제공하도록 요청할 수 있는 경우를 제외하고, 라이선시 또는 그 대리인의 개인정보를 처리하지 않으며 이에 접근하지 않는다. 라이선시는 ABBYY에게 해당 개인정보 및/또는 기술 정보를 제공하지 않기로 선택할 수 있으며, 이 경우 라이선시는 자신의 개인정보 및/또는 기술 정보를 제공하는 ABBYY 고객에게 제공되는 ABBYY 소프트웨어에 대한 지원 및 유지보수 서비스를 취득할 수 없을 수 있다. 단, 해당 개인정보 및/또는 기술 정보가 라이선시에게 ABBYY 소프트웨어에 대한 지원 또는 유지보수를 제공하는 데 필수적이거나 ABBYY 소프트웨어에 관한 지원 및 유지보수의 요건이며 관련 법률과 모순되지 않는 경우에 한한다. 라이선시는 ABBYY 또는 승인된 출처가 요구하는 것 이상의 개인정보 및/또는 기술 정보를 제공하지 않기로 동의하며, 제공된 개인정보 및/또는 기술 정보가 관련 법률에 따라 데이터의 기밀성 및 데이터 보안이 유지되는 경우 ABBYY 및/또는 그 계열사 또는 파트너에 의해 처리(수집 및/또는 기타 사용을 포함하되 이에 한정되지 않음)될 수 있음에 동의한다. 본 조에서 설명된 개인정보를 ABBYY가 처리하는 방법에 대한 자세한 내용은 https://www.abbyy.com/privacy/에서 확인할 수 있는 ABBYY 개인정보 처리방침("개인정보 처리방침")을 참조한다. 라이선시가 개별 최종 사용자의 개인정보 수집 및 해당 관할권 외부로의 데이터 전송에 대해 지정된 주체가 책임져야 한다고 요구하는 현지 법률이 있는 지역에서 ABBYY 소프트웨어를 사용하는 경우, 라이선시는 자신이 해당 법률을 준수할 책임이 있는 주체임을 인정한다.',
    },
    {
      segmentId: 86,
      sourceText:
        "5.3.2. ABBYY does not have access to any Personal Data in the documents that Licensee processes using ABBYY Software. Licensee acknowledges that ABBYY Software may have periodic Internet connection with an ABBYY server to check the license status or download additional content, information or components. ABBYY will take reasonable steps with the objective that no Personal Data of Licensee will be transferred during such connections. Licensee acknowledges that when Licensee uses ABBYY Software, data about Licensee’s use of ABBYY Software and/or resources may be automatically logged on ABBYY’s servers, including information about Licensee’s system (operating system, access dates and times), a model of computer/device and, if applicable, a locale of computer/device and the history data of use of ABBYY Software. Such data does not include Personal Data in the documents that Licensee processes using ABBYY Software. Some types of Protection Keys could require uninterruptable access to the Internet to manage the applicable restrictions on Usage Rights. The aforementioned information about the use of ABBYY Software be used for security (e.g., fraud prevention) and statistical (e.g., billing, and analytical) purposes.",
      translatedText:
        "5.3.2. ABBYY는 라이선시가 ABBYY 소프트웨어를 사용하여 처리하는 문서의 개인정보에 접근할 수 없다. 라이선시는 ABBYY 소프트웨어가 라이선스 상태를 확인하거나 추가 콘텐츠, 정보 또는 구성 요소를 다운로드하기 위해 ABBYY 서버와 주기적으로 인터넷 연결을 할 수 있음을 인정한다. ABBYY는 이러한 연결 중에 라이선시의 개인정보가 전송되지 않도록 하기 위해 합리적인 조치를 취한다. 라이선시는 ABBYY 소프트웨어를 사용할 때, 라이선시의 ABBYY 소프트웨어 및/또는 자원 사용에 관한 데이터가 ABBYY 서버에 자동으로 로그될 수 있음을 인정하며, 여기에는 라이선시의 시스템(운영 체제, 접속 일자 및 시간), 컴퓨터/장치 모델 및 해당되는 경우 컴퓨터/장치의 지역 설정 및 ABBYY 소프트웨어 사용 이력 데이터가 포함된다. 이러한 데이터는 라이선시가 ABBYY 소프트웨어를 사용하여 처리하는 문서의 개인정보를 포함하지 않는다. 일부 유형의 보호 키는 사용권에 대한 적용 가능한 제한을 관리하기 위해 인터넷에 대한 중단 없는 접속이 필요할 수 있다. ABBYY 소프트웨어 사용에 관한 상기 정보는 보안(예: 사기 방지) 및 통계(예: 청구 및 분석) 목적으로 사용될 수 있다.",
    },
    {
      segmentId: 87,
      sourceText:
        "5.3.3. ABBYY may send Licensee e-mails with product and company news, information about special offers, advice on product usage and other product and company-related information provided ABBYY has a legitimate ground for this (e.g., Licensee’s consent to receive such information). Licensee may remove its e-mail address from ABBYY’s mailing list at any time by clicking on the unsubscribe link located at the bottom of each such email. Licensee may also receive from ABBYY e-mail messages with critical information related to ABBYY Software operation (password related e-mails, service notifications, etc.) that are needed to provide you with ABBYY Software.",
      translatedText:
        "5.3.3. ABBYY는 ABBYY가 이에 대한 정당한 근거(예: 해당 정보 수령에 대한 라이선시의 동의)가 있는 경우, 라이선시에게 제품 및 회사 뉴스, 특별 제안에 대한 정보, 제품 사용에 대한 조언 및 기타 제품 및 회사 관련 정보를 포함한 이메일을 발송할 수 있다. 라이선시는 각 이메일 하단에 위치한 구독 취소 링크를 클릭하여 언제든지 자신의 이메일 주소를 ABBYY의 메일링 리스트에서 제거할 수 있다. 또한 라이선시는 ABBYY로부터 ABBYY 소프트웨어를 제공하는 데 필요한 ABBYY 소프트웨어 운영과 관련된 중요 정보(비밀번호 관련 이메일, 서비스 알림 등)가 포함된 이메일 메시지를 수신할 수 있다.",
    },
    {
      segmentId: 88,
      sourceText:
        "5.4 Feedback. Licensee may provide feedback to ABBYY regarding testing and use of ABBYY Software (including Evaluation Versions), including error reporting, ideas or suggestions or bug reports (“Feedback”), in which case Licensee grants to ABBYY a worldwide, perpetual, transferrable, royalty free license to use all such Feedback, including without limitation, the rights to use, publish and disseminate Feedback at ABBYY’s discretion. ABBYY may use any Feedback Licensee provides in connection with its use of ABBYY Software as part of ABBYY’s business operations and its marketing materials.",
      translatedText:
        '5.4 피드백. 라이선시는 ABBYY 소프트웨어(평가 버전 포함)의 테스트 및 사용에 관하여 오류 보고, 아이디어나 제안 또는 버그 보고(이하 "피드백")를 포함한 피드백을 ABBYY에 제공할 수 있다. 이 경우 라이선시는 ABBYY에게 모든 피드백에 대한 전세계적이고 영구적이며 양도 가능하고 로열티 없는 라이선스를 부여하며, 이에는 ABBYY의 재량에 따라 피드백을 사용, 공표 및 배포할 권리가 포함되나 이에 한정되지 아니한다. ABBYY는 라이선시가 ABBYY 소프트웨어 사용과 관련하여 제공하는 피드백을 ABBYY의 사업 운영 및 마케팅 자료의 일부로 사용할 수 있다.',
    },
    {
      segmentId: 89,
      sourceText: "6. Indemnification",
      translatedText: "6. 면책",
    },
    {
      segmentId: 90,
      sourceText:
        "6.1 Claims. Subject to exceptions of Section 6.2, ABBYY will indemnify Licensee against the final court’s judgment or any settlements out of any third-party claim against Licensee alleging that Licensee’s use of ABBYY Software pursuant to Usage Rights infringes a third-party intellectual property right (the “IP Claim”), provided that Licensee shall: (a) promptly (whenever practicable but no later than 7 days from its receipt) notify ABBYY in writing of the IP Claim, and promptly (whenever practicable but no later than 7 days from the receipt of ABBYY’s request) provide ABBYY with information and documents reasonably required for the defense of the IP Claim; (b) fully cooperate with ABBYY in the defense of the IP Claim; and (c) grant ABBYY the full control of the defense, appeal and settlement. ABBYY will have no obligation to reimburse Licensee for any costs incurred prior to ABBYY's receipt of notification of the IP Claim. This Section 6.1 provides for Licensee’s sole and exclusive remedy and ABBYY’s sole liability for any IP Claims against Licensee.",
      translatedText:
        '6.1 청구. 제6.2항의 예외를 전제로 하여, ABBYY는 라이선시가 사용권에 따라 ABBYY 소프트웨어를 사용하는 것이 제3자의 지식재산권을 침해한다고 주장하는 제3자의 청구(이하 "지식재산권 청구")에 대한 최종 법원 판결 또는 합의에 대해 라이선시를 면책한다. 다만, 라이선시는 다음을 이행하여야 한다: (a) 지식재산권 청구에 대해 신속하게(실행 가능한 경우에는 언제든지, 그러나 수령일로부터 7일 이내에) 서면으로 ABBYY에 통지하고, 지식재산권 청구의 방어에 합리적으로 필요한 정보 및 문서를 신속하게(실행 가능한 경우에도, 그러나 ABBYY의 요청을 수령한 날로부터 7일 이내에) ABBYY에 제공하여야 한다; (b) 지식재산권 청구 방어에 대해 ABBYY와 완전히 협력하여야 한다; (c) 방어, 항소 및 합의에 대한 완전한 통제권을 ABBYY에 부여하여야 한다. ABBYY는 ABBYY가 지식재산권 청구에 대한 통지를 수령하기 전에 발생한 비용을 라이선시에게 상환할 의무가 없다. 본 제6.1항은 라이선시에 대한 지식재산권 청구에 관한 라이선시의 유일하고 배타적인 구제수단 및 ABBYY의 단독 책임을 규정한다.',
    },
    {
      segmentId: 91,
      sourceText:
        "6.2 Exclusions. ABBYY has no indemnity obligation with respect to any IP Claim based on or in connection with: (a) modification of ABBYY Software by Licensee or by a third party; (b) compliance with any specifications or requirements by Licensee or by a third party; (c) duration of potential use of the ABBYY Software, Licensee’s potential revenues or services; (d) Licensee’s combination, operation, or use of ABBYY Software with non-ABBYY products, or business processes; (e) Licensee’s failure to modify or replace ABBYY Software as required from time to time by ABBYY; or (f) any ABBYY Software provided as an Evaluation Version or otherwise for free.",
      translatedText:
        "6.2 제외. ABBYY는 다음에 근거하거나 이와 관련된 지식재산권 청구에 대해 면책 의무를 부담하지 않는다: (a) 라이선시 또는 제3자에 의한 ABBYY 소프트웨어의 수정; (b) 라이선시 또는 제3자의 사양 또는 요건 준수; (c) ABBYY 소프트웨어의 잠재적 사용 기간, 라이선시의 잠재적 수익 또는 서비스; (d) 라이선시가 ABBYY 소프트웨어를 비ABBYY 제품 또는 사업 프로세스와 결합, 운영 또는 사용하는 경우; (e) 라이선시가 수시로 ABBYY가 요구하는 대로 ABBYY 소프트웨어를 수정 또는 교체하지 않는 경우; 또는 (f) 평가 버전 또는 기타 무료로 제공되는 ABBYY 소프트웨어.",
    },
    {
      segmentId: 92,
      sourceText: "7. Warranties and Representations",
      translatedText: "7. 보증 및 진술",
    },
    {
      segmentId: 93,
      sourceText:
        "7.1 Performance. For a period of ninety (90) days from the first delivery of ABBYY Software to Licensee, ABBYY warrants that the core functions of ABBYY Software perform in all material respects as stated in the accompanying Documentation and Product Specific Terms.",
      translatedText:
        "7.1 이행. ABBYY 소프트웨어가 라이선시에게 최초로 인도된 날로부터 90일 동안, ABBYY는 첨부된 문서 및 제품 특정 조건에 명시된 바와 같이 ABBYY 소프트웨어의 핵심 기능이 모든 중대한 측면에서 수행됨을 보증한다.",
    },
    {
      segmentId: 94,
      sourceText:
        "7.2 Malicious Code. For a period of ninety (90) days from the latest delivery of ABBYY Software to Licensee, ABBYY warrants that it has performed industrially standard scan to deliver ABBYY Software free of any code that is designed or intended to disable or impede the normal operation of, or provide unauthorized access to, networks, systems, or ABBYY Software other than to enforce applicable licensing limitations.",
      translatedText:
        "7.2 악성 코드. ABBYY는 라이선시에게 ABBYY 소프트웨어를 최종 인도한 날로부터 90일 동안, 적용 가능한 라이선스 제한을 집행하기 위한 경우를 제외하고, 네트워크, 시스템 또는 ABBYY 소프트웨어의 정상적인 운영을 비활성화하거나 방해하거나 무단 접근을 제공하도록 설계되거나 의도된 코드가 없는 ABBYY 소프트웨어를 인도하기 위해 산업 표준 스캔을 수행하였음을 보증한다.",
    },
    {
      segmentId: 95,
      sourceText:
        "7.3 Qualifications. Sections 7.1 and 7.2 do not apply if ABBYY Software: (a) has been altered, except by ABBYY; (b) has been subjected to accident or negligence, or installation or use inconsistent with this Agreement or Documentation; (c) is an Evaluation Version or otherwise is provided for free; (d) is not an ABBYY-branded product; or (e) has not been provided by an Approved Source. THE FOREGOING WARRANTIES DO NOT APPLY, AND ABBYY STRICTLY DISCLAIMS ALL WARRANTIES, WITH RESPECT TO ANY THIRD-PARTY MATERIALS.",
      translatedText:
        "7.3 자격 요건. 제7.1조 및 제7.2조는 ABBYY 소프트웨어가 다음 중 어느 하나에 해당하는 경우에는 적용되지 않는다: (a) ABBYY에 의한 경우를 제외하고 변경된 경우, (b) 사고 또는 과실의 대상이 되거나 본 계약 또는 문서와 일치하지 않는 설치 또는 사용이 이루어진 경우, (c) 평가 버전이거나 기타 무료로 제공된 경우, (d) ABBYY 브랜드 제품이 아닌 경우, 또는 (e) 승인된 출처에 의해 제공되지 않은 경우. 상기 보증은 제3자 자료에 대해서는 적용되지 않으며, ABBYY는 제3자 자료에 대한 모든 보증을 엄격히 부인한다.",
    },
    {
      segmentId: 96,
      sourceText:
        "7.4 Exclusive Remedy. Upon Licensee’s prompt written notification to the Approved Source during the warranty period set in Section 7.1 or 7.2, Licensee’s sole and exclusive remedy (unless otherwise required by applicable law) for its warranty claim is, at ABBYY’s option to either (i) repair or replace ABBYY Software or (ii) refund the Subscription fee paid for the non-conforming ABBYY Software in the past 12 months. Where ABBYY provides a refund of Subscription fees paid to ABBYY, Licensee must return or destroy all copies of the respective ABBYY Software.",
      translatedText:
        "7.4 배타적 구제수단. 제7.1조 또는 제7.2조에 명시된 보증 기간 동안 라이선시가 승인된 출처에 신속하게 서면으로 통지한 경우, 라이선시의 보증 청구에 대한 유일하고 배타적인 구제수단은 (관련 법률에 따라 달리 요구되지 않는 한) ABBYY의 선택에 따라 (i) ABBYY 소프트웨어를 수리 또는 교체하거나 (ii) 최근 12개월 동안 부적합한 ABBYY 소프트웨어에 대해 지급된 구독료를 환불하는 것이다. ABBYY가 ABBYY에 지급된 구독료를 환급하는 경우, 라이선시는 해당 ABBYY 소프트웨어의 모든 사본을 반환하거나 파기해야 한다.",
    },
    {
      segmentId: 97,
      sourceText:
        "7.5 Disclaimer. Except FOR THE LIMITED WARRANTY SET FORTH IN Section 7, to the extent allowed by applicable law, SOFTWARE AND DOCUMENTATION ARE PROVIDED TO LICENSEE “AS IS” AND WITH ALL FAULTS AND DEFECTS WITHOUT WARRANTY OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED UNDER APPLICABLE LAW, ABBYY, ON ITS OWN BEHALF AND ON BEHALF OF ITS AFFILIATES AND ITS AND THEIR RESPECTIVE LICENSORS AND SERVICE PROVIDERS, EXPRESSLY DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, WITH RESPECT TO ABBYY SOFTWARE AND DOCUMENTATION, INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT, AND WARRANTIES THAT MAY ARISE OUT OF COURSE OF DEALING, COURSE OF PERFORMANCE, USAGE, OR TRADE PRACTICE. WITHOUT LIMITATION TO THE FOREGOING, ABBYY PROVIDES NO WARRANTY OR UNDERTAKING, AND MAKES NO REPRESENTATION OF ANY KIND THAT ABBYY SOFTWARE WILL MEET THE LICENSEE’S REQUIREMENTS, ACHIEVE ANY INTENDED RESULTS, BE COMPATIBLE, OR WORK WITH ANY OTHER SOFTWARE, APPLICATIONS, SYSTEMS, OR SERVICES, OPERATE WITHOUT INTERRUPTION, MEET ANY PERFORMANCE OR RELIABILITY STANDARDS OR BE ERROR FREE, OR THAT ANY ERRORS OR DEFECTS CAN OR WILL BE CORRECTED.",
      translatedText:
        '7.5 면책. 제7조에 명시된 제한적 보증을 제외하고, 관련 법률이 허용하는 범위 내에서, 소프트웨어 및 문서는 라이선시에게 "있는 그대로" 제공되며 모든 결함 및 하자가 포함되어 있으며 어떠한 종류의 보증도 없다. 관련 법률에 따라 허용되는 최대한의 범위 내에서, ABBYY는 자신과 그 계열사 및 각자의 라이선서 및 서비스 제공자를 대리하여 ABBYY 소프트웨어 및 문서에 대한 모든 명시적, 묵시적, 법정 또는 기타 보증을 명시적으로 부인하며, 여기에는 상품성, 특정 목적에 대한 적합성, 소유권 및 비침해에 대한 모든 묵시적 보증과 거래 과정, 이행 과정, 사용 또는 거래 관행에서 발생할 수 있는 보증이 포함된다. 전술한 내용에 한정되지 않고, ABBYY는 ABBYY 소프트웨어가 라이선시의 요구사항을 충족하고, 의도된 결과를 달성하며, 다른 소프트웨어, 애플리케이션, 시스템 또는 서비스와 호환되거나 작동하고, 중단 없이 운영되며, 성능 또는 신뢰성 기준을 충족하거나 오류가 없고, 오류 또는 결함이 수정될 수 있거나 수정될 것이라는 어떠한 종류의 보증이나 약속도 제공하지 않으며, 진술하지 않는다.',
    },
    {
      segmentId: 98,
      sourceText: "8. Liability",
      translatedText: "8. 책임",
    },
    {
      segmentId: 99,
      sourceText: "TO THE FULLEST EXTENT PERMITTED UNDER APPLICABLE LAW:",
      translatedText: "관련 법률에 따라 허용되는 최대한의 범위 내에서:",
    },
    {
      segmentId: 100,
      sourceText:
        "8.1 Limitation on Damages. IN NO EVENT SHALL EITHER PARTY OR ITS DIRECTORS, OFFICERS, EMPLOYEES, AGENTS OR AFFILIATES, BE LIABLE TO THE OTHER PARTY FOR ANY CONSEQUENTIAL, INCIDENTAL, INDIRECT, SPECIAL, PUNITIVE OR EXEMPLARY DAMAGES, COSTS OR EXPENSES (INCLUDING, BUT NOT LIMITED TO, LOST PROFITS, LOST REVENUES, LOST SAVINGS AND/OR WASTED EXPENDITURE), WHETHER BASED UPON A CLAIM OR ACTION OF CONTRACT, WARRANTY, NEGLIGENCE, STRICT LIABILITY OR OTHERWISE, ARISING FROM A BREACH OR ALLEGED BREACH OF THIS AGREEMENT, OR THE USE OF ABBYY SOFTWARE.",
      translatedText:
        "8.1 손해배상의 제한. 어떠한 경우에도 어느 당사자나 그 이사, 임원, 직원, 대리인 또는 계열사는 본 계약의 위반 또는 위반 주장이나 ABBYY 소프트웨어의 사용으로 인해 발생하는 결과적, 부수적, 간접적, 특별, 징벌적 또는 예시적 손해, 비용 또는 경비(이익 손실, 수익 손실, 저축 손실 및 낭비된 지출을 포함하되 이에 한정되지 않음)에 대해 계약, 보증, 과실, 엄격책임 또는 기타 청구나 소송의 근거 여부를 불문하고 상대방에게 책임을 지지 아니한다.",
    },
    {
      segmentId: 101,
      sourceText:
        "8.2 Liability Cap. in no event shall ABBYY’s total liability, for any and all claims arising from or related this Agreement, exceed the lesser of (A) total subscription fees paid by Licensee for abbyy Software in the twelve (12) month period immediately preceding the date upon which the claim first arose or (B) FIFTY thousand united states dollars ($50,000.00), in either case EVEN IF ABBYY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.",
      translatedText:
        "8.2 책임 한도. 어떠한 경우에도 본 계약으로부터 발생하거나 이와 관련된 모든 청구에 대한 ABBYY의 총 책임은 (A) 청구가 최초로 발생한 날 직전 12개월 동안 라이선시가 ABBYY 소프트웨어에 대해 지급한 총 구독료 또는 (B) 미국 달러 오만 달러($50,000.00) 중 적은 금액을 초과하지 아니하며, ABBYY가 해당 손해의 가능성에 대해 통지를 받았더라도 마찬가지이다.",
    },
    {
      segmentId: 102,
      sourceText:
        "8.3 Applicability. THE FOREGOING DISCLAIMERS AND LIMITATIONS OF LIABILITY SHALL APPLY EVEN IF THE PARTIES HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES AND EVEN IF THE PARTY’S REMEDIES UNDER THIS AGREEMENT FAIL OF THEIR ESSENTIAL PURPOSE. THE FOREGOING DISCLAIMERS AND LIMITATIONS OF LIABILITY SHALL NOT APPLY TO MISAPPROPRIATION OF ABBYY SOFTWARE.",
      translatedText:
        "8.3 적용 가능성. 상기 면책 및 책임 제한은 당사자들이 해당 손해의 가능성을 고지받았거나 본 계약에 따른 당사자의 구제수단이 본질적 목적을 달성하지 못한 경우에도 적용된다. 상기 면책 및 책임 제한은 ABBYY 소프트웨어의 오용에 적용되지 않는다.",
    },
    {
      segmentId: 103,
      sourceText: "9. Termination and Suspension",
      translatedText: "9. 해지 및 중지",
    },
    {
      segmentId: 104,
      sourceText:
        "9.1 This Agreement shall continue in effect for an initial period (“Initial Term”) starting on the Effective Date and ending 12 months after the Effective Date. This Agreement will be automatically renewed for an unlimited number of successive term of 12 months each (“Renewal Term”) unless either Party notifies the other Party of its decision not to renew by giving a written notice 2 months prior to the date of expiration of the Initial Term or a written notice 2 months prior to the date of expiration of then current Renewal Term.",
      translatedText:
        '9.1 본 계약은 효력 발생일에 시작하여 효력 발생일로부터 12개월 후에 종료되는 최초 기간(이하 "최초 기간") 동안 유효하다. 본 계약은 일방 당사자가 최초 기간 만료일 2개월 전 또는 당시 갱신 기간 만료일 2개월 전에 서면 통지를 통해 갱신하지 않기로 결정하는 경우를 제외하고, 각각 12개월의 무제한 연속 기간(이하 "갱신 기간")으로 자동 갱신된다.',
    },
    {
      segmentId: 105,
      sourceText:
        "9.2 Termination. Either party may terminate this Agreement if the other party breaches any material provision of this Agreement and if such breach is not cured within thirty (30) days of notice from the non-breaching party. In addition, ABBYY reserves the right to immediately suspend or terminate all or any of its obligations and this Agreement at any time by giving notice in writing to the Licensee if any of the following applies:",
      translatedText:
        "9.2 해지. 일방 당사자는 상대방 당사자가 본 계약의 중대한 조항을 위반하고, 해당 위반이 비위반 당사자의 통지로부터 30일 이내에 시정되지 않는 경우 본 계약을 해지할 수 있다. 또한, ABBYY는 다음 중 어느 하나에 해당하는 경우 라이선시에게 서면 통지를 통해 언제든지 자신의 의무 및 본 계약의 전부 또는 일부를 즉시 정지하거나 해지할 권리를 보유한다:",
    },
    {
      segmentId: 106,
      sourceText:
        "9.2.1 Licensee commits and fails to cure within thirty (30) days from the date ABBYY notifies Licensee of a breach of Sections 2.1, 2.2, 2.3, 3.1, or 11.7 of this Agreement;",
      translatedText:
        "9.2.1 라이선시가 본 계약 제2.1조, 제2.2조, 제2.3조, 제3.1조 또는 제11.7조를 위반하고 ABBYY가 라이선시에게 위반 사실을 통지한 날로부터 30일 이내에 이를 시정하지 아니하는 경우",
    },
    {
      segmentId: 107,
      sourceText:
        "9.2.2 Licensee develops and/or distributes software containing ABBYY Software other than the specified usage in the Application; or",
      translatedText:
        "9.2.2 라이선시가 애플리케이션에서 명시된 용도 이외에 ABBYY 소프트웨어를 포함하는 소프트웨어를 개발 및/또는 배포하는 경우 또는",
    },
    {
      segmentId: 108,
      sourceText:
        "9.2.3 \tLicensee becomes bankrupt or insolvent, or suffers any similar action in consequence of debt or goes into voluntary or compulsory liquidation to the extent permitted under applicable law.",
      translatedText:
        "9.2.3 라이선시가 파산 또는 지급불능 상태가 되거나, 채무로 인하여 이와 유사한 조치를 받거나, 관련 법률에서 허용하는 범위 내에서 임의 또는 강제 청산 절차에 들어가는 경우",
    },
    {
      segmentId: 109,
      sourceText:
        "9.3 Effects of Termination or Expiration.. Promptly upon any expiration or earlier termination of this Agreement Licensee shall not use or distribute the Application and ABBYY Software, including without limitation its documentation and any component or portion of ABBYY Software or its Documentation. Licensee shall pay ABBYY in full the balance due on the account if the Agreement is terminated through to the end of the then-current Subscription Term. At the end of the Agreement and if the Agreement is not renewed, Licensee will delete and destroy all Software and copies of Software installed on their computer(s). Licensee will then email to ABBYY a confirmation via a Letter of Destruction signed by an authorized representative of Licensee. If Licensee continues to use or access any ABBYY Software after termination of this Agreement, ABBYY or the Approved Source may invoice Licensee, and Licensee agrees to pay, for such continued use.",
      translatedText:
        "9.3 해지 또는 만료의 효과. 본 계약이 만료되거나 조기 해지되는 즉시 라이선시는 애플리케이션 및 ABBYY 소프트웨어(그 문서 및 ABBYY 소프트웨어 또는 그 문서의 구성 요소나 일부를 포함하되 이에 한정되지 아니함)를 사용하거나 배포하여서는 아니 된다. 본 계약이 해지되는 경우 라이선시는 당시 구독 기간 종료 시까지 계정의 미지급 잔액을 ABBYY에 전액 지급하여야 한다. 계약 종료 시 계약이 갱신되지 아니하는 경우, 라이선시는 자신의 컴퓨터에 설치된 모든 소프트웨어 및 소프트웨어 사본을 삭제하고 파기한다. 라이선시는 라이선시의 권한 있는 대표자가 서명한 파기확인서를 통하여 ABBYY에 확인 내용을 이메일로 송부한다. 라이선시가 본 계약 해지 후에도 ABBYY 소프트웨어를 계속 사용하거나 이에 접근하는 경우, ABBYY 또는 승인된 공급원은 라이선시에게 그러한 지속적 사용에 대하여 청구할 수 있으며, 라이선시는 이를 지급하기로 한다.",
    },
    {
      segmentId: 110,
      sourceText:
        "9.4 Survival. The expiration or earlier termination of this Agreement shall not affect any of its provisions which are expressed to operate or have effect after termination or any right of action already accrued to ABBYY in respect of any breach by Licensee, including without limitation all obligations concerning confidentiality, payments, and reconciliation. The following sections also shall survive any termination or expiration of this Agreement or any Quote entered into hereunder: Sections 3, 5 - 11 survive the termination of this Agreement.",
      translatedText:
        "9.4 존속. 본 계약의 만료 또는 조기 해지는 해지 후에 효력을 발생시키는 것으로 명시된 조항이나 라이선시의 위반과 관련하여 ABBYY에 이미 발생한 소송권(비밀유지, 지급 및 화해에 관한 모든 의무를 포함하되 이에 한정되지 않음)에 영향을 미치지 아니한다. 다음 조항도 본 계약 또는 본 계약에 따라 체결된 견적서의 해지 또는 만료 후에도 존속한다: 제3조, 제5조부터 제11조까지는 본 계약의 해지 후에도 존속한다.",
    },
    {
      segmentId: 111,
      sourceText:
        "9.5 Consequences of Subscription Termination. Licensee shall make sure, that Authorized Users and/or End Users do not use ABBYY Software after termination of Subscription Term or after the expiration of Subscription Term. For avoidance of doubt, upon termination of the Agreement Subscription Term will not autorenew.",
      translatedText:
        "9.5 구독 해지의 결과. 라이선시는 수권사용자 및/또는 최종사용자가 구독기간 종료 후 또는 만료 후 ABBYY 소프트웨어를 사용하지 아니하도록 하여야 한다. 명확히 하자면, 계약의 해지 시 구독기간은 자동으로 갱신되지 아니한다.",
    },
    {
      segmentId: 112,
      sourceText:
        "9.6 Application Licenses After Termination. Licenses to copies of Application already distributed to End Users shall survive any termination by either Party (other than for reason of Licensee’s uncured material breach of this Agreement, in which case such licenses shall terminate immediately without further notice) or expiration of this Agreement until the expiration of the Subscription Term of the respective Subscription.",
      translatedText:
        "9.6 해지 후 애플리케이션 라이선스. 이미 최종사용자에게 배포된 애플리케이션 사본에 대한 라이선스는 일방 당사자에 의한 해지(라이선시의 치유되지 아니한 본 계약의 중대한 위반으로 인한 경우를 제외하고, 이 경우 해당 라이선스는 추가 통지 없이 즉시 해지됨) 또는 본 계약의 만료 후에도 존속하며, 각 구독의 구독기간이 만료될 때까지 유효하다.",
    },
    {
      segmentId: 113,
      sourceText: "10. Audit Rights",
      translatedText: "10. 감사권",
    },
    {
      segmentId: 114,
      sourceText:
        "During the Subscription Term and for a period of 3 years after its expiration, Licensee shall make reasonable efforts to maintain complete and accurate records of Licensee’s use of the ABBYY Software and, where applicable, the actual records of Volume Units processed with ABBYY Software sufficient to verify compliance with this Agreement (“Records”). Not more frequently than once per 12-month period, Licensee will, within 30 days from ABBYY’s advance notice, allow ABBYY and/or its auditors at ABBYY’s cost access to the Records and any applicable books, systems (including ABBYY Software), and accounts during Licensee’s normal business hours. If the audit process reveals underpayment of fees: (a) Licensee shall pay such underpaid fees within 15 days from the ABBYY’s shortfall invoice date; and (b) Licensee shall also pay the reasonable cost of the audit if the fees owed to ABBYY as a result of such audit exceed the amounts paid for Licensee’s Usage Rights by more than 5%.",
      translatedText:
        '구독 기간 동안 및 그 만료 후 3년 동안, 라이선시는 본 계약의 준수를 검증하기에 충분한 ABBYY 소프트웨어 사용에 대한 완전하고 정확한 기록 및 해당되는 경우 ABBYY 소프트웨어로 처리된 볼륨 단위의 실제 기록(이하 "기록")을 유지하기 위해 합리적인 노력을 기울여야 한다. 라이선시는 12개월 기간 동안 1회를 초과하지 않는 빈도로, ABBYY의 사전 통지일로부터 30일 이내에 ABBYY 및/또는 그 감사인이 ABBYY의 비용으로 라이선시의 통상적인 영업 시간 동안 기록 및 관련 장부, 시스템(ABBYY 소프트웨어 포함) 및 계좌에 접근할 수 있도록 허용하여야 한다. 감사 과정에서 수수료의 과소 지급이 발견되는 경우: (a) 라이선시는 ABBYY의 부족분 청구서 발행일로부터 15일 이내에 과소 지급된 수수료를 지급하여야 하며; (b) 해당 감사의 결과로 ABBYY에 지급하여야 하는 수수료가 라이선시의 사용권에 대해 지급된 금액을 5% 이상 초과하는 경우, 라이선시는 감사의 합리적인 비용도 지급하여야 한다.',
    },
    {
      segmentId: 115,
      sourceText: "11. General Provisions",
      translatedText: "11. 일반 조항",
    },
    {
      segmentId: 116,
      sourceText:
        "11.1 Relationship of the Parties; No Third-Party Beneficiaries. The parties are independent business entities, owing to each other the rights and obligations set forth in this Agreement. No partnership, joint venture, agency or employment relationship is established by this Agreement. This Agreement does not grant any right or cause of action to any third party.",
      translatedText:
        "11.1 당사자들의 관계; 제3자 수익자의 부존재. 당사자들은 본 계약에 명시된 권리 및 의무를 상호 부담하는 독립적인 사업체이다. 본 계약에 의해 파트너십, 합작투자, 대리 또는 고용 관계가 설정되지 아니한다. 본 계약은 제3자에게 어떠한 권리나 소송 원인도 부여하지 아니한다.",
    },
    {
      segmentId: 117,
      sourceText:
        "11.2 Assignment and Subcontracting. Except as set out below, neither party may assign or novate this Agreement in whole or in part without the other party’s express written consent. ABBYY may (a) by providing a notice to Licensee pursuant to Section 11.6, assign or novate this Agreement in whole or in part to an ABBYY Affiliate, or otherwise as part of a sale or transfer of any part of its business; or (b) subcontract any performance associated with ABBYY Software to third parties, provided that such subcontract does not relieve ABBYY of any of its obligations under this Agreement. No delegation or other transfer will relieve Licensee of any of its obligations or performance under this Agreement. Any purported assignment, delegation, or transfer in violation of this Section 11.2 is void. This Agreement is binding upon and inures to the benefit of the parties hereto and their respective permitted successors and assigns.",
      translatedText:
        "11.2 양도 및 하도급. 아래에 명시된 경우를 제외하고, 어느 당사자도 상대방 당사자의 명시적인 서면 동의 없이 본 계약의 전부 또는 일부를 양도하거나 갱신할 수 없다. ABBYY는 (a) 제11.6조에 따라 라이선시에게 통지함으로써 본 계약의 전부 또는 일부를 ABBYY 계열사에 양도하거나 갱신하거나, 기타 사업의 일부 매각 또는 이전의 일환으로 양도할 수 있으며; 또는 (b) ABBYY 소프트웨어와 관련된 이행을 제3자에게 하도급할 수 있으나, 이러한 하도급은 ABBYY를 본 계약상 의무로부터 면제하지 아니한다. 어떠한 위임이나 기타 양도도 라이선시를 본 계약상 의무 또는 이행으로부터 면제하지 아니한다. 본 제11.2조를 위반하여 이루어진 양도, 위임 또는 이전은 무효이다. 본 계약은 본 계약 당사자 및 그들의 허용된 승계인 및 양수인에게 구속력을 가지며 그들의 이익을 위하여 효력을 발생한다.",
    },
    {
      segmentId: 118,
      sourceText:
        "11.3 Indirect Transactions. Notwithstanding other provisions here and unless otherwise agreed by ABBYY in writing, if Licensee purchases ABBYY Software from a Partner then the terms of this Agreement apply to Licensee’s use of such ABBYY Software and prevail over any inconsistent provisions in Licensee’s agreement with the Partner. All disputes between Licensee and such Partner, whether or not relating to ABBYY Software, any information, data or availability of support and maintenance services shall be settled by and between Licensee and such Partner. ABBYY bears no responsibility for resolving such disputes and has no obligation to be involved in any way in any such dispute.",
      translatedText:
        "11.3 간접 거래. 본 계약의 다른 조항에도 불구하고 ABBYY가 서면으로 달리 합의하지 아니하는 한, 라이선시가 파트너로부터 ABBYY 소프트웨어를 구매하는 경우 본 계약의 조건이 라이선시의 해당 ABBYY 소프트웨어 사용에 적용되며 라이선시와 파트너 간의 계약의 불일치하는 조항에 우선한다. ABBYY 소프트웨어, 정보, 데이터 또는 지원 및 유지보수 서비스의 이용 가능성과 관련 여부에 관계없이 라이선시와 해당 파트너 간의 모든 분쟁은 라이선시와 해당 파트너 간에 해결되어야 한다. ABBYY는 이러한 분쟁을 해결할 책임을 지지 아니하며 이러한 분쟁에 어떠한 방식으로도 관여할 의무를 지지 아니한다.",
    },
    {
      segmentId: 119,
      sourceText:
        "11.4 Entire Agreement and Amendments. This Agreement, including all Appendixes, Exhibits and attachments, is intended as the complete, final and exclusive statement of the terms of the agreement between the parties and supersedes all prior understandings, writings, proposals, representations or communications, oral or written, relating to the subject matter hereof. This Agreement may not be modified except in a writing executed by both parties. Although Licensee may use its standard purchase order and ABBYY may use its standard order acknowledgement and invoice, the terms and conditions of this Agreement will prevail over such forms, and any additional, inconsistent, conflicting, or different terms in such forms will be void and of no force or effect. Any terms on Licensee’s purchase orders except for those concerning price and quantity are expressly rejected and shall be void.",
      translatedText:
        "11.4 완전한 계약 및 수정. 모든 부록, 별첨 및 첨부물을 포함한 본 계약은 당사자 간의 계약 조건에 대한 완전하고 최종적이며 배타적인 진술로서 본 계약의 주제와 관련된 모든 이전의 구두 또는 서면 양해, 서면, 제안, 진술 또는 의사소통을 대체한다. 본 계약은 양 당사자가 서명한 서면에 의하지 아니하고는 수정될 수 없다. 라이선시가 표준 구매 주문서를 사용할 수 있고 ABBYY가 표준 주문 확인서 및 청구서를 사용할 수 있으나 본 계약의 조건이 해당 양식에 우선하며 해당 양식의 추가적이거나 불일치하거나 상충하거나 상이한 조건은 무효이며 효력을 갖지 아니한다. 가격 및 수량에 관한 조건을 제외한 라이선시의 구매 주문서상의 모든 조건은 명시적으로 거부되며 무효로 한다.",
    },
    {
      segmentId: 120,
      sourceText:
        "11.5 Compliance with Laws. Each party will comply with all laws and regulations applicable to their respective obligations under this Agreement. ABBYY may restrict the availability of ABBYY Software in any particular location or modify or discontinue features to comply with applicable laws and regulations.",
      translatedText:
        "11.5 법률 준수. 각 당사자는 본 계약에 따른 각자의 의무에 적용되는 모든 법률 및 규정을 준수하여야 한다. ABBYY는 적용 법률 및 규정을 준수하기 위해 특정 지역에서 ABBYY 소프트웨어의 이용 가능성을 제한하거나 기능을 수정 또는 중단할 수 있다.",
    },
    {
      segmentId: 121,
      sourceText:
        "11.6 Notice. Any notice required or permitted under this Agreement shall be in English and in writing and shall be given by: (i) personal delivery; or (ii) registered or certified state mail, with postage prepaid, return receipt requested; or (iii) by an internationally recognized overnight courier service. Notice shall be effective upon receipt. Notices to ABBYY or to Licensee shall be addressed to each of them at their respective addresses above stated, or to such other address as either of them may hereafter specify by notice given in accordance with the provisions of this paragraph. In the case of ABBYY, any such notice shall be addressed to the attention of its General Counsel and a copy of it shall be sent to legaloperations@abbyy.com.",
      translatedText:
        "11.6 통지. 본 계약에 따라 요구되거나 허용되는 통지는 영어로 서면으로 작성되어야 하며, 다음 방법으로 제공되어야 한다: (i) 직접 전달; 또는 (ii) 우편요금 선납 및 반송 수령증이 요청된 등기우편 또는 배달증명우편; 또는 (iii) 국제적으로 인정된 익일 택배 서비스. 통지는 수령 시 효력이 발생한다. ABBYY 또는 라이선시에 대한 통지는 위에 명시된 각자의 주소 또는 본 항의 규정에 따라 통지로써 이후 명시하는 기타 주소로 발송되어야 한다. ABBYY의 경우, 해당 통지는 법무담당자 앞으로 발송되어야 하며, 그 사본은 legaloperations@abbyy.com으로 송부되어야 한다.",
    },
    {
      segmentId: 122,
      sourceText:
        "11.7 Trade Restrictions. Licensee acknowledges and agrees that ABBYY Software, Documentation, related technologies, and any derivatives thereof are subject to U.S. and other countries’ export control and sanctions laws and regulations (“Trade Restrictions”). Licensee agrees to comply with all applicable Trade Restrictions in performance of this Agreement and not to re-export ABBYY Software, Documentation, related technologies, and any derivatives thereof in violation of Trade Restrictions. Licensee further represents that Licensee is not a Restricted Party.",
      translatedText:
        "11.7 무역 제한. 라이선시는 ABBYY 소프트웨어, 문서, 관련 기술 및 그 파생물이 미국 및 기타 국가의 수출 통제 및 제재 법률과 규정(이하 무역 제한)의 적용을 받음을 인정하고 이에 동의한다. 라이선시는 본 계약을 이행함에 있어 모든 적용 가능한 무역 제한을 준수하고 무역 제한을 위반하여 ABBYY 소프트웨어, 문서, 관련 기술 및 그 파생물을 재수출하지 아니하기로 한다. 라이선시는 또한 자신이 제한대상 당사자가 아님을 진술한다.",
    },
    {
      segmentId: 123,
      sourceText:
        '“Restricted Party” means any person or entity that is: (i) located, organized or a resident of a comprehensively sanctioned country or territory (including Syria, Sudan and Darfur, Iran, North Korea, Somalia, Yemen, Cuba, Venezuela, Russia, or Belarus) ("Sanctioned Territory"); (ii) is owned or controlled by or acting on behalf of a Sanctioned Territory’s government; (iii) is identified on any U.S., E.U., or multilateral restricted party lists; or (iv) is owned or controlled by, or acting on behalf of, any of the foregoing.',
      translatedText:
        "제한대상 당사자는 다음 중 어느 하나에 해당하는 개인 또는 법인을 의미한다: (i) 포괄적 제재 대상 국가 또는 지역(시리아, 수단 및 다르푸르, 이란, 북한, 소말리아, 예멘, 쿠바, 베네수엘라, 러시아 또는 벨라루스 포함)(이하 제재대상 지역)에 소재하거나 설립되거나 거주하는 자; (ii) 제재대상 지역의 정부에 의해 소유되거나 지배되거나 이를 대리하여 행위하는 자; (iii) 미국, 유럽연합 또는 다자간 제한대상 당사자 목록에 기재된 자; 또는 (iv) 전술한 자에 의해 소유되거나 지배되거나 이를 대리하여 행위하는 자.",
    },
    {
      segmentId: 124,
      sourceText:
        "Licensee shall not and shall not permit any Authorized User to access, use, or make ABBYY Software, Documentation, related technologies, and any derivatives thereof available to or by any Restricted Party or to, from, or in connection with any Sanctioned Territory. Licensee shall indemnify and hold ABBYY harmless from and against any claims, liabilities, or costs that arise from Licensee’s violation of Trade Restrictions.",
      translatedText:
        "라이선시는 수권 사용자가 제한 대상 당사자 또는 제재 지역과 관련하여 ABBYY 소프트웨어, 문서, 관련 기술 및 그 파생물에 접근하거나 사용하거나 이용할 수 있도록 하여서는 아니 되며, 이를 허용하여서도 아니 된다. 라이선시는 무역 제한 위반으로 인해 발생하는 모든 청구, 책임 또는 비용으로부터 ABBYY를 면책하고 보호하여야 한다.",
    },
    {
      segmentId: 125,
      sourceText:
        "Licensee shall not host technical data or software controlled for export, reexport, or in-country transfer under Trade Restrictions on any ABBYY service without first receiving express consent from ABBYY. Any refusal or suspension of ABBYY’s performance of this Agreement that arises from its obligations to comply with Trade Restrictions will not constitute a breach this Agreement and Licensee waives any and all claims against ABBYY for any legal recourse, including, but not limited to, injunctive or declarative relief, loss, cost or expense, including consequential damages, that Licensee may incur or be subject to as a result of such refusal or suspension.",
      translatedText:
        "라이선시는 ABBYY의 명시적 동의를 먼저 받지 아니하고는 무역 제한에 따라 수출, 재수출 또는 국내 이전이 통제되는 기술 데이터 또는 소프트웨어를 ABBYY 서비스에 호스팅하여서는 아니 된다. 무역 제한 준수 의무로 인해 발생하는 ABBYY의 본 계약 이행 거부 또는 중단은 본 계약 위반을 구성하지 아니하며, 라이선시는 이러한 거부 또는 중단의 결과로 발생할 수 있는 금지명령 또는 선언적 구제, 손실, 비용 또는 경비(결과적 손해 포함)를 비롯한 모든 법적 구제에 대한 ABBYY에 대한 모든 청구를 포기한다.",
    },
    {
      segmentId: 126,
      sourceText:
        "11.8 Governing Law and Venue. This Agreement shall be governed by and construed in accordance with the laws of England and Wales. Any dispute arising out of or in connection with this contract, including any question regarding its existence, validity or termination, shall be referred to and finally resolved by arbitration administered by the Singapore International Arbitration Centre (SIAC) in accordance with the Arbitration Rules of the Singapore International Arbitration Centre (SIAC Rules) for the time being in force, which rules are deemed to be incorporated by reference in this clause. The seat of the arbitration shall be Singapore. The Tribunal shall consist of three arbitrators. The language of the arbitration shall be English. The parties agree that the United Nations Convention on Contracts for the International Sale of Goods is specifically excluded from application to this Agreement. Each party hereby consents and submits to the exclusive jurisdiction of such courts. Notwithstanding the choice of governing law and venue above, ABBYY may seek interim injunctive relief in any court of appropriate jurisdiction with respect to any alleged breach of ABBYY’s intellectual property rights.",
      translatedText:
        "11.8 준거법 및 재판지. 본 계약은 잉글랜드 및 웨일스 법률에 따라 규율되고 해석된다. 본 계약의 존재, 유효성 또는 해지에 관한 문제를 포함하여 본 계약으로부터 또는 본 계약과 관련하여 발생하는 모든 분쟁은 현재 유효한 싱가포르국제중재센터(SIAC)의 중재규칙(SIAC 규칙)에 따라 싱가포르국제중재센터가 관리하는 중재에 회부되어 최종적으로 해결되며, 해당 규칙은 본 조항에 참조로 포함된 것으로 간주된다. 중재의 소재지는 싱가포르로 한다. 중재판정부는 3명의 중재인으로 구성된다. 중재의 언어는 영어로 한다. 당사자들은 국제물품매매계약에 관한 유엔협약이 본 계약의 적용에서 특별히 제외됨에 동의한다. 각 당사자는 이에 따라 해당 법원의 배타적 관할권에 동의하고 복종한다. 상기 준거법 및 재판지의 선택에도 불구하고 ABBYY는 ABBYY의 지식재산권 침해 주장과 관련하여 적절한 관할권을 가진 법원에서 임시 금지명령 구제를 요청할 수 있다.",
    },
    {
      segmentId: 127,
      sourceText:
        "11.9 Force Majeure. In no event shall either Party be liable to the other Party, or be deemed to have breached this Agreement, for any failure or delay in performing its obligations under this Agreement, except for any obligations to make payments, if and to the extent such failure or delay is caused by any circumstances beyond such Party’s reasonable control, including but not limited to: (i) acts of God; (ii) flood, fire, earthquake, or explosion; (iii) war, invasion, hostilities (whether war is declared or not), terrorist threats or acts, riot or other civil unrest; (iv) government order, law, or actions; (v) embargoes or blockades in effect on or after the date of this Agreement; (vi) national or regional emergency; and (vii) strikes, labor stoppages or slowdowns, or other industrial disturbances.",
      translatedText:
        "11.9 불가항력. 지급 의무를 제외하고, 해당 당사자의 합리적 통제를 벗어난 상황으로 인해 본 계약상 의무의 불이행 또는 지연이 발생한 경우, 어느 당사자도 상대방에게 책임을 지지 아니하며 본 계약을 위반한 것으로 간주되지 아니한다. 여기에는 다음이 포함되나 이에 한정되지 아니한다: (i) 천재지변, (ii) 홍수, 화재, 지진 또는 폭발, (iii) 전쟁, 침략, 적대행위(전쟁 선포 여부 무관), 테러 위협 또는 행위, 폭동 또는 기타 시민 불안, (iv) 정부 명령, 법률 또는 조치, (v) 본 계약 체결일 이후 유효한 금수조치 또는 봉쇄, (vi) 국가 또는 지역 비상사태, (vii) 파업, 노동 중단 또는 둔화, 또는 기타 산업 소란.",
    },
    {
      segmentId: 128,
      sourceText:
        "11.10 Waiver. Failure of either party to enforce compliance with any provision of this Agreement shall not constitute a waiver of such provision unless accompanied by a clear written statement that such provision is waived. A waiver of any default hereunder or of any of the terms and conditions of this Agreement shall not be deemed to be a continuing waiver or a waiver of any other default or of any other term or condition, but shall apply solely to the instance to which such waiver is directed. Except as expressly provided herein to the contrary, the exercise of any right or remedy provided in this Agreement shall be without prejudice to any other right or remedy provided by law or equity.",
      translatedText:
        "11.10 포기. 일방 당사자가 본 계약의 어느 조항에 대한 준수를 강제하지 않는다고 하여 해당 조항이 포기되었다는 명확한 서면 진술이 수반되지 않는 한 해당 조항의 포기를 구성하지 않는다. 본 계약에 따른 채무불이행 또는 본 계약의 조건에 대한 포기는 지속적인 포기나 다른 채무불이행 또는 다른 조건에 대한 포기로 간주되지 않으며, 해당 포기가 지향하는 사안에만 적용된다. 본 계약에 명시적으로 달리 규정된 경우를 제외하고, 본 계약에 규정된 권리 또는 구제수단의 행사는 법률 또는 형평법에 의해 제공되는 다른 권리 또는 구제수단을 침해하지 않는다.",
    },
    {
      segmentId: 129,
      sourceText:
        "11.11 Order of precedence. In case of a conflict between Quote, Agreement, Product Specific Terms, Subscription Terms, and the SM Terms, the documents shall prevail in the following order: (1) the Quote, (2) the Product Specific Terms, (3) the Subscription Terms, (4) the SM Terms and (5) the main body of Agreement.",
      translatedText:
        "11.11 우선순위. 견적서, 계약, 제품별 조건, 구독 조건 및 SM 조건 간에 상충이 발생하는 경우, 해당 문서들은 다음 순서로 우선한다: (1) 견적서, (2) 제품별 조건, (3) 구독 조건, (4) SM 조건 및 (5) 계약 본문.",
    },
    {
      segmentId: 130,
      sourceText:
        "11.12 Interpretation. For purposes of this Agreement, (a) the words “include,” “includes,” and “including” shall be deemed to be followed by the words “without limitation”; (b) the word “or” is not exclusive; and (c) the words “herein,” “hereof,” “hereby,” “hereto,” and “hereunder” refer to this Agreement as a whole. Unless the context otherwise requires, references herein: (x) to Sections, Annexes, Schedules, and Exhibits refer to the Sections of, and Annexes, Schedules, and Exhibits attached to, this Agreement; (y) to an agreement, instrument, or other document means such agreement, instrument, or other document as amended, supplemented, and modified from time to time to the extent permitted by the provisions thereof and (z) to a statute means such statute as amended from time to time and includes any successor legislation thereto and any regulations promulgated thereunder. This Agreement shall be construed without regard to any presumption or rule requiring construction or interpretation against the party drafting an instrument or causing any instrument to be drafted. The Quote, the Subscription Terms, the SM Terms and all Annexes, Schedules, and Exhibits referred to herein shall be construed with, and as an integral part of, this Agreement to the same extent as if they were set forth verbatim herein. The headings in this Agreement are for reference only and do not affect the interpretation of this Agreement. All the terms and time periods herein are calculated as calendar if not explicitly specified otherwise.",
      translatedText:
        "11.12 해석. 본 계약의 목적상, (a) 포함한다, 포함하며 및 포함하는이라는 단어는 제한 없이라는 단어가 뒤따르는 것으로 간주하며, (b) 또는이라는 단어는 배타적이지 않으며, (c) 본 계약에서, 본 계약의, 이에 의해, 본 계약에 및 본 계약에 따라라는 단어는 본 계약 전체를 지칭한다. 문맥상 달리 요구되지 않는 한, 본 계약에서의 언급은 다음과 같다: (x) 조, 부록, 일정표 및 별첨은 본 계약의 조 및 본 계약에 첨부된 부록, 일정표 및 별첨을 지칭하며, (y) 계약, 증서 또는 기타 문서는 해당 조항이 허용하는 범위에서 수시로 개정, 보완 및 수정된 해당 계약, 증서 또는 기타 문서를 의미하며, (z) 법령은 수시로 개정된 해당 법령을 의미하고 그에 대한 후속 법령 및 그에 따라 공포된 규정을 포함한다. 본 계약은 증서를 작성하거나 증서가 작성되도록 한 당사자에게 불리한 해석이나 구성을 요구하는 추정이나 규칙에 관계없이 해석된다. 견적서, 구독 조건, SM 조건 및 본 계약에서 언급된 모든 부록, 일정표 및 별첨은 본 계약에 문언으로 명시된 것과 동일한 범위에서 본 계약과 함께 그리고 본 계약의 불가분의 일부로 해석된다. 본 계약의 표제는 참조용일 뿐이며 본 계약의 해석에 영향을 미치지 않는다. 본 계약의 모든 조건 및 기간은 달리 명시적으로 규정되지 않는 한 역일로 계산된다.",
    },
    {
      segmentId: 131,
      sourceText:
        "11.13 Counterparts. This Agreement may be executed in counterparts, each of which is deemed an original, but all of which together are deemed to be one and the same agreement. A signed copy of this Agreement delivered by facsimile, email, or other means of electronic transmission is deemed to have the same legal effect as delivery of an original signed copy of this Agreement.",
      translatedText:
        "11.13 부본. 본 계약은 부본으로 체결될 수 있으며, 각각은 원본으로 간주되지만 모두 함께 하나의 동일한 계약으로 간주된다. 팩스, 이메일 또는 기타 전자적 전송 수단으로 전달된 본 계약의 서명된 사본은 본 계약의 서명된 원본 사본의 전달과 동일한 법적 효력을 가지는 것으로 간주된다.",
    },
    {
      segmentId: 132,
      sourceText: "Executed by the Parties as described below:",
      translatedText: "아래와 같이 당사자들에 의하여 체결됨:",
    },
    {
      segmentId: 133,
      sourceText: "ABBYY:",
      translatedText: "ABBYY:",
    },
    {
      segmentId: 134,
      sourceText: "Licensee:",
      translatedText: "라이선시:",
    },
    {
      segmentId: 135,
      sourceText: "By:",
      translatedText: "작성자:",
    },
    {
      segmentId: 136,
      sourceText: "By:",
      translatedText: "작성자:",
    },
    {
      segmentId: 137,
      sourceText: "Title:",
      translatedText: "직위:",
    },
    {
      segmentId: 138,
      sourceText: "Title:",
      translatedText: "직위:",
    },
    {
      segmentId: 139,
      sourceText: "Date:",
      translatedText: "일자:",
    },
    {
      segmentId: 140,
      sourceText: "Date:",
      translatedText: "일자:",
    },
    {
      segmentId: 141,
      sourceText: "Signature:",
      translatedText: "서명:",
    },
    {
      segmentId: 142,
      sourceText: "Signature:",
      translatedText: "서명:",
    },
    {
      segmentId: 143,
      sourceText: "Appendix A",
      translatedText: "부록 A",
    },
    {
      segmentId: 144,
      sourceText: "Commercial Terms",
      translatedText: "상업약관",
    },
    {
      segmentId: 145,
      sourceText: "1. Approved Source: ABBYY.",
      translatedText: "1. 승인된 출처: ABBYY.",
    },
    {
      segmentId: 146,
      sourceText: "2. Territory: Worldwide.",
      translatedText: "2. 지역: 전 세계.",
    },
    {
      segmentId: 147,
      sourceText: "3. Licensed Use: ISV Distribution.",
      translatedText: "3. 라이선스 사용: ISV 유통.",
    },
    {
      segmentId: 148,
      sourceText: "3.1. Application Name: BeringAI.",
      translatedText: "3.1. 출원명: BeringAI.",
    },
    {
      segmentId: 149,
      sourceText:
        "3.2. Application Description: Enterprise AI Translation platform.",
      translatedText: "3.2. 출원 설명: 기업용 AI 번역 플랫폼.",
    },
    {
      segmentId: 150,
      sourceText: "PRODUCT SPECIFIC TERMS – ABBYY FineReader Engine",
      translatedText: "제품 특정 조건 – ABBYY FineReader 엔진",
    },
    {
      segmentId: 151,
      sourceText: "1. Additional Definitions",
      translatedText: "1. 추가 정의",
    },
    {
      segmentId: 152,
      sourceText:
        "“ABBYY Software” means ABBYY® FineReader® Engine for Windows, ABBYY® FineReader® Engine for Linux, ABBYY® FineReader® Engine for Mac OS including any and all software components (add-ons, custom components, and modules, etc.) embedded in the software or made available online or other ways such as: executable, help, demo, sample and other files; libraries, databases, samples, associated media (images, photos, animation, audio-video components, music etc.), Documentation, and other software components.",
      translatedText:
        '"ABBYY 소프트웨어"는 소프트웨어에 내장되거나 온라인 또는 기타 방법으로 이용 가능한 모든 소프트웨어 구성 요소(애드온, 커스텀 구성 요소 및 모듈 등)를 포함하여 Windows용 ABBYY® FineReader® Engine, Linux용 ABBYY® FineReader® Engine 및 Mac OS용 ABBYY® FineReader® Engine을 의미하며, 여기에는 실행 파일, 도움말, 데모, 샘플 및 기타 파일, 라이브러리, 데이터베이스, 샘플, 관련 미디어(이미지, 사진, 애니메이션, 오디오-비디오 구성 요소, 음악 등), 문서 및 기타 소프트웨어 구성 요소가 포함된다.',
    },
    {
      segmentId: 153,
      sourceText:
        "“Activation” means the procedure whereby Protection Key is checked for validity and the installed ABBYY Software is made fully operational in accordance with Documentation and the scope of Usage Rights. Activation may be performed either: (i) by means of a USB dongle as hardware Protection Key plugged into the Computer where ABBYY Software is used; (ii) by means of a software Protection Key, including Online License, on Physical Workplace or Virtual Workplace where ABBYY Software is used.",
      translatedText:
        '"활성화"는 보호 키의 유효성을 확인하고 설치된 ABBYY 소프트웨어가 문서 및 사용권 범위에 따라 완전히 작동하도록 하는 절차를 의미한다. 활성화는 (i) ABBYY 소프트웨어가 사용되는 컴퓨터에 연결된 하드웨어 보호 키인 USB 동글을 통하거나, (ii) ABBYY 소프트웨어를 사용하는 물리적 근무지 또는 가상 근무지에서 온라인 라이선스를 포함한 소프트웨어 보호 키를 통해 수행될 수 있다.',
    },
    {
      segmentId: 154,
      sourceText:
        "“Activated Protection Key” means the Protection Key for which Activation was successfully performed.",
      translatedText:
        '"활성화된 보호 키"는 활성화가 성공적으로 수행된 보호 키를 의미한다.',
    },
    {
      segmentId: 155,
      sourceText:
        "“Application” means the computer program, or set of computer programs, developed by Licensee, or on Licensee’s behalf, which includes parts of ABBYY Software or otherwise interacts with ABBYY Software.",
      translatedText:
        '"애플리케이션"은 ABBYY 소프트웨어의 일부를 포함하거나 ABBYY 소프트웨어와 상호작용하는 라이선시 또는 라이선시를 대리하여 개발된 컴퓨터 프로그램 또는 컴퓨터 프로그램 세트를 의미한다.',
    },
    {
      segmentId: 156,
      sourceText:
        "“Computer” means a specific physical device or Virtual Machine that may consist of one or more CPU (central processing unit) cores and running a specified operating system. Any changes to the configuration or composition of Computer may result in Computer being considered a different Computer for licensing purposes.",
      translatedText:
        '"컴퓨터"란 하나 이상의 CPU(중앙처리장치) 코어로 구성되고 특정 운영체제를 실행하는 특정 물리적 장치 또는 가상 머신을 의미한다. 컴퓨터의 구성 또는 구성의 변경은 라이선스 목적상 다른 컴퓨터로 간주될 수 있다.',
    },
    {
      segmentId: 157,
      sourceText:
        "“Online License” means Software activation over the internet, without Your involvement or support. It is a license key on ABBYY’s side for which a renewable permission(s) is provided to You to use Software over the internet.",
      translatedText:
        '"온라인 라이선스"는 귀하의 개입이나 지원 없이 인터넷을 통해 소프트웨어를 활성화하는 것을 의미한다. 이는 귀하가 인터넷을 통해 소프트웨어를 사용할 수 있도록 갱신 가능한 허가가 제공되는 ABBYY 측의 라이선스 키이다.',
    },
    {
      segmentId: 158,
      sourceText:
        "“Physical Workplace” means one copy of computer programs and operating system run on Computer without the use of Virtual Machine.",
      translatedText:
        '"물리적 작업장소"란 가상머신을 사용하지 않고 컴퓨터에서 실행되는 컴퓨터 프로그램 및 운영체제의 1개 사본을 의미한다.',
    },
    {
      segmentId: 159,
      sourceText:
        "“Virtual Workplace” means one copy of computer programs and operating system run on Computer by means of Virtual Machine.",
      translatedText:
        '"가상 작업장소"란 가상머신을 통해 컴퓨터에서 실행되는 컴퓨터 프로그램 및 운영체제의 1개 사본을 의미한다.',
    },
    {
      segmentId: 160,
      sourceText:
        "“Workplace” means one (1) Physical Workplace or one (1) Virtual Workplace.",
      translatedText:
        '"작업장소"란 1개의 물리적 작업장소 또는 1개의 가상 작업장소를 의미한다.',
    },
    {
      segmentId: 161,
      sourceText:
        "“Virtual Machine” means any computer program that isolates applications executed within an operating system from Computer, enabling the user to save and restore the original state of the computer programs and the operating system, create their copies, and run them.",
      translatedText:
        '"가상머신"이란 운영체제 내에서 실행되는 애플리케이션을 컴퓨터로부터 격리하여 사용자가 컴퓨터 프로그램 및 운영체제의 원래 상태를 저장 및 복구하고 그 사본을 생성하여 실행할 수 있도록 하는 컴퓨터 프로그램을 말한다.',
    },
    {
      segmentId: 162,
      sourceText: "2. License Grant and Usage Restrictions",
      translatedText: "2. 라이선스 부여 및 사용 제한",
    },
    {
      segmentId: 163,
      sourceText:
        "2.1 Types of Licenses. The following two types of licenses to ABBYY Software may be granted for Licensee’s Licensed Use only: (i) Developer License providing for the right to integrate parts of ABBYY Software into the Application; and (ii) Runtime License providing for the right to reproduce parts of ABBYY Software integrated into Application.",
      translatedText:
        "2.1 라이선스 유형. ABBYY 소프트웨어에 대한 다음 두 가지 유형의 라이선스는 라이선시의 라이선스 사용을 위해서만 부여될 수 있다: (i) ABBYY 소프트웨어의 일부를 애플리케이션에 통합할 권리를 제공하는 개발자 라이선스 및 (ii) 애플리케이션에 통합된 ABBYY 소프트웨어의 일부를 복제할 권리를 제공하는 런타임 라이선스",
    },
    {
      segmentId: 164,
      sourceText:
        "2.2 Activation. In order to use ABBYY Software Licensee shall perform Activation of ABBYY Software.",
      translatedText:
        "2.2 활성화. ABBYY 소프트웨어를 사용하기 위해 라이선시는 ABBYY 소프트웨어의 활성화를 수행하여야 한다",
    },
    {
      segmentId: 165,
      sourceText:
        "2.3 Usage Restrictions. Restrictions on Licensee’s use of ABBYY Software and the scope of Usage Rights may include, but are not limited to, the following:",
      translatedText:
        "2.3 사용 제한. 라이선시의 ABBYY 소프트웨어 사용 및 사용권의 범위에 대한 제한은 다음 중 어느 하나를 포함하되 이에 한정되지 아니한다:",
    },
    {
      segmentId: 166,
      sourceText:
        "2.3.1. Number of Activations. ABBYY Software requires an Activated Protection Key to be used. Licensee’s ability to perform the Activation on a Virtual Workplace may be limited. Additionally, if Protection Key is activated on a Virtual Workplace, the use of ABBYY Software on public cloud services (i.e. Microsoft Azure and Amazon Web Services) is subject to an additional ABBYY written approval.",
      translatedText:
        "2.3.1. 활성화 횟수. ABBYY 소프트웨어는 활성화된 보호키를 사용하여야 한다. 라이선시가 가상작업장에서 활성화를 수행하는 능력은 제한될 수 있다. 또한 보호키가 가상작업장에서 활성화된 경우, 공공 클라우드 서비스(즉 Microsoft Azure 및 Amazon Web Services)에서 ABBYY 소프트웨어를 사용하는 것은 ABBYY의 추가 서면 승인을 받아야 한다",
    },
    {
      segmentId: 167,
      sourceText:
        "2.3.2. Processing volume. The number of Volume Units that may be processed by ABBYY Software may be limited in one or more ways, such as limitations on the number of Volume Units being placed that may be processed within time periods, such as monthly, quarterly or annually, or on the overall number of Volume Units that may be processed. The size of a page and the number of data fields per document may also be limited.",
      translatedText:
        "2.3.2. 처리량. ABBYY 소프트웨어가 처리할 수 있는 볼륨 단위의 수는 월별, 분기별 또는 연간과 같은 기간 내에 처리할 수 있는 배치된 볼륨 단위의 수 제한 또는 처리할 수 있는 전체 볼륨 단위의 수 제한과 같은 하나 이상의 방법으로 제한될 수 있다. 페이지 크기 및 문서당 데이터 필드의 수도 제한될 수 있다",
    },
    {
      segmentId: 168,
      sourceText:
        "2.3.3. Processing Power. The number of CPU (central processing unit) cores of Computers which can be utilized by ABBYY Software may be limited by the Protection Key and/or a written agreement between Licensee and Approved Source, and/or applicable Quote.",
      translatedText:
        "2.3.3. 처리능력. ABBYY 소프트웨어가 활용할 수 있는 컴퓨터의 CPU(중앙처리장치) 코어의 수는 보호키 및/또는 라이선시와 승인된 공급원 간의 서면 계약 및/또는 해당 견적서에 의해 제한될 수 있다",
    },
    {
      segmentId: 169,
      sourceText:
        "2.3.4. Installation Method. Licensee’s ability to install and use ABBYY Software on multiple Workplaces may be limited. For instance, limitations may provide for ABBYY Software to be used: (i) on any Workplace with a network connection to the Workplace where Protection Key is Activated (“concurrent licensing”); or (ii) on the same Workplace where Protection Key is Activated (“standalone licensing”). In case of standalone licensing various terminal server users running ABBYY Software may be either allowed or not to jointly utilize one set of CPU cores.",
      translatedText:
        '2.3.4. 설치방법. 라이선시가 여러 작업장에서 ABBYY 소프트웨어를 설치하고 사용하는 능력은 제한될 수 있다. 예를 들어 ABBYY 소프트웨어가 (i) 보호키가 활성화된 작업장에 네트워크로 연결된 모든 작업장에서("동시 라이선스") 또는 (ii) 보호키가 활성화된 동일한 작업장에서("독립형 라이선스") 사용되도록 제한될 수 있다. 독립형 라이선스의 경우 ABBYY 소프트웨어를 실행하는 여러 터미널 서버 사용자가 하나의 CPU 코어 세트를 공동으로 활용하는 것이 허용되거나 허용되지 아니할 수 있다',
    },
    {
      segmentId: 170,
      sourceText:
        "2.4 If Licensee deploys ABBYY Software by using terminal services which allow accessing ABBYY Software on a remote Computer over a network, the amount of simultaneously running instances of ABBYY Software may not exceed the number of Computers for which Usage Rights have been obtained.",
      translatedText:
        "2.4 라이선시가 네트워크를 통해 원격 컴퓨터에서 ABBYY 소프트웨어에 접근할 수 있도록 하는 터미널 서비스를 사용하여 ABBYY 소프트웨어를 배포하는 경우, ABBYY 소프트웨어의 동시 실행 인스턴스 수는 사용권을 취득한 컴퓨터 수를 초과하여서는 아니 된다.",
    },
    {
      segmentId: 171,
      sourceText:
        "2.5 If Licensee uses ABBYY Software by means of a web browser, Licensee may simultaneously access ABBYY Software by as many Computers as permitted by the scope of Usage Rights and specified in Protection Key and/or written agreement between Licensee and Approved Source.",
      translatedText:
        "2.5 라이선시가 웹 브라우저를 통해 ABBYY 소프트웨어를 사용하는 경우, 라이선시는 사용권의 범위에 의해 허용되고 보호키 및/또는 라이선시와 승인된 소스 간의 서면 계약에 명시된 수만큼의 컴퓨터로 ABBYY 소프트웨어에 동시에 접근할 수 있다.",
    },
    {
      segmentId: 172,
      sourceText:
        "2.6 Licensee shall display the following copyright and trademark notices in Application documentation:",
      translatedText:
        "2.6 라이선시는 애플리케이션 문서에 다음의 저작권 및 상표 고지를 표시하여야 한다:",
    },
    {
      segmentId: 173,
      sourceText:
        "This software includes ABBYY® FineReader® Engine recognition technologies © 2023 ABBYY Development Inc. ABBYY, ABBYY FINEREADER and FINEREADER are either registered trademarks or trademarks of ABBYY Development Inc. and/or its affiliates. These designations can also be logos, product or company names (or part of any of the above) of ABBYY Development Inc. and/or its affiliates and may not be used without consent of their respective owners.",
      translatedText:
        "본 소프트웨어는 ABBYY® FineReader® 엔진 인식 기술을 포함합니다 © 2023 ABBYY Development Inc. ABBYY, ABBYY FINEREADER 및 FINEREADER는 ABBYY Development Inc. 및/또는 그 계열사의 등록상표 또는 상표입니다. 이러한 명칭은 또한 ABBYY Development Inc. 및/또는 그 계열사의 로고, 제품 또는 회사명(또는 이들 중 일부)일 수 있으며 각 소유자의 동의 없이 사용될 수 없습니다.",
    },
    {
      segmentId: 174,
      sourceText:
        "2.7 Subcontractors. Licensee may hire or engage one or more subcontractors to develop an Application; provided, that (i) ABBYY will not be required to grant any license to Licensee subcontractors (ii) each such Licensee’s subcontractor is bound by all limitations on the Usage Rights scope; and (iii) Licensee shall in all cases remain responsible for its subcontractors compliance with the terms of this Agreement.",
      translatedText:
        "2.7 하도급업체. 라이선시는 애플리케이션을 개발하기 위해 하나 이상의 하도급업체를 고용하거나 참여시킬 수 있다. 다만, (i) ABBYY는 라이선시 하도급업체에게 어떠한 라이선스도 부여할 의무가 없고 (ii) 라이선시의 각 하도급업체는 사용권 범위에 대한 모든 제한에 구속되며 (iii) 라이선시는 모든 경우에 하도급업체의 본 계약 조건 준수에 대해 책임을 진다.",
    },
    {
      segmentId: 175,
      sourceText: "PRODUCT SPECIFIC TERMS – ABBYY FineReader Server",
      translatedText: "제품별 조건 – ABBYY FineReader 서버",
    },
    {
      segmentId: 176,
      sourceText: "1. Additional definitions:",
      translatedText: "1. 추가 정의:",
    },
    {
      segmentId: 177,
      sourceText:
        '"ABBYY Software" means ABBYY® FineReader® Server including any and all of the software components embedded in it or made available online or in other ways, including, but not limited to, executable, help, demo, sample, and other files; libraries, databases, samples, associated media (images, photos, animations, audio components, video components, music, etc.), Documentation, and other software components.',
      translatedText:
        '"ABBYY 소프트웨어"는 실행 파일, 도움말, 데모, 샘플 및 기타 파일, 라이브러리, 데이터베이스, 샘플, 관련 미디어(이미지, 사진, 애니메이션, 오디오 구성요소, 비디오 구성요소, 음악 등), 문서 및 기타 소프트웨어 구성요소를 포함하되 이에 한정되지 않는, 내장되거나 온라인 또는 기타 방식으로 이용 가능한 모든 소프트웨어 구성요소를 포함한 ABBYY® FineReader® 서버를 의미한다.',
    },
    {
      segmentId: 178,
      sourceText:
        '"Google Search Appliance" means a network device manufactured by Google, Inc. and intended for searching information in a local area network.',
      translatedText:
        '"구글 검색 어플라이언스"는 Google, Inc.가 제조하고 지역 네트워크에서 정보를 검색하기 위한 네트워크 장치를 의미한다.',
    },
    {
      segmentId: 179,
      sourceText:
        '"Google Search Appliance Connector" means Software Component which passes the content of recognized documents to the Google Search Appliance.',
      translatedText:
        '"구글 검색 어플라이언스 커넥터"는 인식된 문서의 내용을 구글 검색 어플라이언스에 전달하는 소프트웨어 구성 요소를 의미한다.',
    },
    {
      segmentId: 180,
      sourceText:
        '"IFilter" means Software Component which passes the content of recognized documents to Microsoft® Office SharePoint® Server, Microsoft Search Server, Windows Search, or Desktop Search.',
      translatedText:
        '"I필터"란 인식된 문서의 내용을 Microsoft® Office SharePoint® Server, Microsoft Search Server, Windows Search 또는 Desktop Search에 전달하는 소프트웨어 구성요소를 의미한다.',
    },
    {
      segmentId: 181,
      sourceText:
        '"Indexing Station" means Software Component for specifying document types and assigning document attributes.',
      translatedText:
        '"인덱싱 스테이션"이란 문서 유형을 지정하고 문서 속성을 할당하기 위한 소프트웨어 구성요소를 의미한다.',
    },
    {
      segmentId: 182,
      sourceText:
        '"Open API" means Software Component which provides an Application Programming Interface to ABBYY Software that allows Licensee to call its functions from and exchange data with other Windows® applications.',
      translatedText:
        '"개방형 API"란 ABBYY 소프트웨어에 애플리케이션 프로그래밍 인터페이스를 제공하여 라이선시가 다른 Windows® 애플리케이션으로부터 기능을 호출하고 데이터를 교환할 수 있도록 하는 소프트웨어 구성요소를 의미한다.',
    },
    {
      segmentId: 183,
      sourceText:
        '"Processing Station" means the computer running the optical character recognition process with ABBYY Software.',
      translatedText:
        '"처리 스테이션"이란 ABBYY 소프트웨어로 광학 문자 인식 프로세스를 실행하는 컴퓨터를 의미한다.',
    },
    {
      segmentId: 184,
      sourceText:
        '"Remote Administration Console" means Software Component which provides a common administration interface for configuring and monitoring ABBYY Software. Remote Administration Console can be located on any computer in a local area network.',
      translatedText:
        '"원격 관리 콘솔"이란 ABBYY 소프트웨어를 구성 및 모니터링하기 위한 공통 관리 인터페이스를 제공하는 소프트웨어 구성 요소를 말한다. 원격 관리 콘솔은 지역 네트워크의 모든 컴퓨터에 위치할 수 있다.',
    },
    {
      segmentId: 185,
      sourceText:
        '"Scanning Station" means Software Component for managing batch scanning, viewing documents and controlling their visual quality, and managing separation and export of documents in selected file formats to selected destinations.',
      translatedText:
        '"스캐닝 스테이션"이란 배치 스캐닝 관리, 문서 열람 및 시각적 품질 제어, 선택된 파일 형식의 문서 분리 및 선택된 목적지로의 수출 관리를 위한 소프트웨어 구성 요소를 말한다.',
    },
    {
      segmentId: 186,
      sourceText: '"Server Computer" means a computer running Server Manager.',
      translatedText: '"서버 컴퓨터"란 서버 관리자를 실행하는 컴퓨터를 말한다.',
    },
    {
      segmentId: 187,
      sourceText:
        '"Server Manager" means Software Component which manages processing options, licenses, distributes tasks among Processing Stations, Verification Stations, and Indexing Stations, and delivers output documents after the processing.',
      translatedText:
        '"서버 관리자"란 처리 옵션 및 라이선스를 관리하고, 처리 스테이션, 검증 스테이션 및 색인 스테이션 간에 작업을 분배하며, 처리 후 출력 문서를 전달하는 소프트웨어 구성 요소를 말한다.',
    },
    {
      segmentId: 188,
      sourceText:
        '"Software Activation" means a procedure whereby the installed ABBYY Software is made fully operational in accordance with Documentation and the scope of Usage Rights.',
      translatedText:
        '"소프트웨어 활성화"란 설치된 ABBYY 소프트웨어가 문서 및 사용권의 범위에 따라 완전히 작동하도록 하는 절차를 말한다.',
    },
    {
      segmentId: 189,
      sourceText:
        '"Software Component" means any constituent part of ABBYY Software, including, but not limited to, any computer programs contained in ABBYY Software.',
      translatedText:
        '"소프트웨어 구성요소"란 ABBYY 소프트웨어에 포함된 컴퓨터 프로그램을 포함하되 이에 한정되지 아니하는 ABBYY 소프트웨어의 구성 부분을 말한다.',
    },
    {
      segmentId: 190,
      sourceText:
        '"Verification Station" means Software Component for viewing and correcting the results of optical character recognition.',
      translatedText:
        '"검증 스테이션"이란 광학 문자 인식 결과를 확인하고 수정하기 위한 소프트웨어 구성요소를 말한다.',
    },
    {
      segmentId: 191,
      sourceText: "2. Usage Restrictions",
      translatedText: "2. 사용 제한",
    },
    {
      segmentId: 192,
      sourceText:
        "2.1. Restrictions on Licensee’s use of ABBYY Software and the scope of Usage Rights may include, but are not limited to, the following:",
      translatedText:
        "2.1. 라이선시의 ABBYY 소프트웨어 사용 및 사용권의 범위에 대한 제한은 다음을 포함하되 이에 한정되지 아니한다:",
    },
    {
      segmentId: 193,
      sourceText:
        "2.1.1 Number of Software Components, seats and network access. The number of Software Components and computers on a network allowed to simultaneous use ABBYY Software may be limited; such limits include but are not limited to the following:",
      translatedText:
        "2.1.1 소프트웨어 구성요소의 수, 시트 및 네트워크 접속. ABBYY 소프트웨어를 동시에 사용할 수 있는 네트워크상의 소프트웨어 구성요소 및 컴퓨터의 수는 제한될 수 있으며, 이러한 제한은 다음을 포함하되 이에 한정되지 아니한다:",
    },
    {
      segmentId: 194,
      sourceText:
        "2.1.1.1 Licensee may only install, run, and use one (1) Server Manager on one (1) Server Computer.",
      translatedText:
        "2.1.1.1 라이선시는 1대의 서버 컴퓨터에 1개의 서버 관리자만을 설치, 실행 및 사용할 수 있다.",
    },
    {
      segmentId: 195,
      sourceText:
        "2.1.1.2 Licensee may install Scanning Station on an unlimited number of computers. The number of Scanning Stations used simultaneously may not exceed the number specified in the respective Protection Key.",
      translatedText:
        "2.1.1.2 라이선시는 스캐닝 스테이션을 무제한의 컴퓨터에 설치할 수 있다. 동시에 사용되는 스캐닝 스테이션의 수는 해당 보호 키에 명시된 수를 초과하여서는 아니 된다.",
    },
    {
      segmentId: 196,
      sourceText:
        "2.1.1.3 Licensee may install Verification Station on an unlimited number of computers. The number of Verification Stations used simultaneously may not exceed the number specified in the respective Protection Key.",
      translatedText:
        "2.1.1.3 라이선시는 검증 스테이션을 무제한의 컴퓨터에 설치할 수 있다. 동시에 사용되는 검증 스테이션의 수는 해당 보호 키에 명시된 수를 초과하여서는 아니 된다.",
    },
    {
      segmentId: 197,
      sourceText:
        "2.1.1.4 Licensee may install Indexing Station on an unlimited number of computers. The number of Indexing Stations used simultaneously may not exceed the number specified in the respective Protection Key.",
      translatedText:
        "2.1.1.4 라이선시는 색인화 스테이션을 무제한의 컴퓨터에 설치할 수 있다. 동시에 사용되는 색인화 스테이션의 수는 해당 보호 키에 명시된 수를 초과하여서는 아니 된다.",
    },
    {
      segmentId: 198,
      sourceText:
        "2.1.1.5 Licensee may install and run one (1) Google Search Appliance Connector on one (1) Server Computer in order to use ABBYY Software in conjunction with Google Search Appliance if such use is allowed by the respective Protection Key.",
      translatedText:
        "2.1.1.5 라이선시는 해당 보호 키에 의해 허용되는 경우 ABBYY 소프트웨어를 Google Search Appliance와 함께 사용하기 위하여 하나의 서버 컴퓨터에 하나의 Google Search Appliance 커넥터를 설치 및 실행할 수 있다.",
    },
    {
      segmentId: 199,
      sourceText:
        "2.1.1.6 Licensee may install and run IFilter on an unlimited number of computers in order to use ABBYY Software in conjunction with Windows Search or Desktop Search, Microsoft Office SharePoint Server or Microsoft Search Server if this is allowed by the respective Protection Key.",
      translatedText:
        "2.1.1.6 라이선시는 해당 보호 키에 의해 허용되는 경우 ABBYY 소프트웨어를 Windows Search 또는 Desktop Search, Microsoft Office SharePoint Server 또는 Microsoft Search Server와 함께 사용하기 위하여 무제한의 컴퓨터에 IFilter를 설치하고 실행할 수 있다.",
    },
    {
      segmentId: 200,
      sourceText:
        "2.1.1.7 Licensee may install, run, and use the Open API and the Remote Administration Console on an unlimited number of computers.",
      translatedText:
        "2.1.1.7 라이선시는 무제한의 컴퓨터에 Open API 및 원격관리콘솔을 설치, 실행 및 사용할 수 있다",
    },
    {
      segmentId: 201,
      sourceText:
        "2.1.2 Processing volume. The number of Volume Units (e.g. pages) that may be processed with ABBYY Software may be limited in one or more ways, such as limitations on the number of Volume Units that may be processed within particular time periods, such as monthly or annually, or on the overall number of Volume Units that may be processed. The size of a page, the number of data fields per document or other Volume Units may also be limited.",
      translatedText:
        "2.1.2 처리량. ABBYY 소프트웨어로 처리할 수 있는 볼륨 단위(예: 페이지) 수는 월간 또는 연간과 같은 특정 기간 내에 처리될 수 있는 볼륨 단위 수에 대한 제한 또는 처리될 수 있는 전체 볼륨 단위 수에 대한 제한과 같이 하나 이상의 방법으로 제한될 수 있다. 페이지 크기, 문서당 데이터 필드 수 또는 기타 볼륨 단위도 제한될 수 있다",
    },
    {
      segmentId: 202,
      sourceText:
        "2.1.3 Processing power. The total number of CPU cores used by ABBYY Software on Processing Stations may not exceed the number specified in Protection Key, Quote and/or a written agreement between Licensee and Approved Source.",
      translatedText:
        "2.1.3 처리능력. 처리스테이션에서 ABBYY 소프트웨어가 사용하는 CPU 코어의 총 수는 보호키, 견적서 및/또는 라이선시와 승인된소스 간의 서면계약에 명시된 수를 초과하여서는 아니 된다",
    },
    {
      segmentId: 203,
      sourceText:
        "2.2 If Licensee accesses ABBYY Software by using terminal services that allow accessing ABBYY Software on a remote computer over a network, the amount of simultaneously running instances of ABBYY Software may not exceed the number of computers for which the Usage Rights have been granted.",
      translatedText:
        "2.2 라이선시가 네트워크를 통해 원격 컴퓨터의 ABBYY 소프트웨어에 접근할 수 있는 터미널서비스를 사용하여 ABBYY 소프트웨어에 접근하는 경우 ABBYY 소프트웨어의 동시 실행 인스턴스 수는 사용권이 부여된 컴퓨터 수를 초과하여서는 아니 된다",
    },
    {
      segmentId: 204,
      sourceText:
        "2.3 Licensee will not be able to use ABBYY Software under this Agreement unless Licensee performs Software Activation.",
      translatedText:
        "2.3 라이선시는 소프트웨어 활성화를 수행하지 아니하는 한 본 계약에 따라 ABBYY 소프트웨어를 사용할 수 없다.",
    },
  ],
}
